import SalesService from './salesService.js'
import ProductService from './productService.js'
import ProductInventoryService from './productInventoryService.js'
import CustomerService from './customerService.js'
import employeeService from './employeeService.js'
import salaryService from './salaryService.js'
import BranchService from './branchService.js'
import jobService from './jobService.js'
import RawMaterialInventoryService from './rawMaterialInventoryService.js'
import returnService from './returnService.js'
import GRNService from './grnService.js'
import SuppliersService from './suppliersService.js'
import SupplierReturnService from './supplierReturnService.js'
import { SalesModel } from '../utils/salesModel.js'

/**
 * Reports Service - Handles all report generation and data processing
 */
class ReportsService {
  constructor() {
    this.reportTypes = {
      'sales-summary': 'Sales Summary',
      'payments': 'Payment Methods',
      'product-summary': 'Product Performance',
      'raw-material-summary': 'Raw Material Summary',
      'rawMaterialSummary': 'Raw Material Summary', // Keep for backward compatibility
      'productMargin': 'Product Margin',
      'stock-alerts': 'Low Stock Alerts',
      'suppliers': 'Suppliers',
      'customers': 'Customer Report',
      'employees': 'Employee Report',
      'salaryReports': 'Salary Reports',
      'productSales': 'Product Sales',
      'stockExpiry': 'Stock Expiry',
      'customerReturns': 'Customer Returns',
      'supplierReturns': 'Supplier Returns',
      'profitsLoss': 'Profits & Loss',
      'daily-sales': 'Daily Sales',
      'product-categories': 'Product Categories',
      'customer-segments': 'Customer Segments',
      'revenue-trends': 'Revenue Trends',
      'top-products': 'Top Products',
      'branch-performance': 'Branch Performance',
      'shift-reports': 'Shift Reports',
      'promotions': 'Promotions Report'
    }
  }

  /**
   * Get all available report types
   */
  getReportTypes() {
    return this.reportTypes
  }

  /**
   * Generate Sales Summary Report  
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} - Sales data
   */
  async generateSalesSummaryReport(filters = {}) {
    try {
      console.log('📊 Generating Sales Summary Report with filters:', filters)
      
      // Only pass orderStatus to API, do all filtering client-side
      const response = await SalesService.getAllSales({
        orderStatus: 'completed'
      })
      
      const parsedData = SalesService.parseResponse(response)
      
      // Handle the new API response structure where orders are in data.orders array
      let orders = []
      if (parsedData.data && parsedData.data.orders) {
        orders = parsedData.data.orders
      } else if (parsedData.orders) {
        orders = parsedData.orders
      } else if (Array.isArray(parsedData.data)) {
        orders = parsedData.data
      } else {
        orders = parsedData.sales || []
      }
      
      console.log('🔍 Orders data received:', orders.length, 'records')
      
      // Transform API orders to sales summary format
      let sales = orders.map(order => {
        // Extract payment method from orderPayment.paymentMethods array
        const paymentMethod = order.orderPayment?.paymentMethods?.[0]?.method || 'cash'
        
        // Calculate total amount from orderPayment
        const total = order.orderPayment?.totalAmount || 0
        
        // Format order type for display
        let orderType = order.orderType || 'dine_in'
        const orderTypeMap = {
          'dine_in': 'Dine In',
          'take_away': 'Take Away',
          'takeaway': 'Take Away',
          'delivery': 'Delivery'
        }
        const displayOrderType = orderTypeMap[orderType] || orderType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
        
        return {
          id: order._id,
          orderNumber: order.orderNumber,
          customerName: order.customer?.name || 'Walking Customer',
          orderType: displayOrderType,
          paymentMethod: paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1),
          total: total,
          date: order.orderDate || order.createdAt,
          status: order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1),
          createdAt: order.createdAt,
          branch: order.branch?.name
        }
      })
      
      console.log('🔍 Transformed sales data:', sales.length, 'records')
      console.log('📝 Sample transformed data:', sales.slice(0, 2))
      
      // Debug: Show unique values in the data
      const uniqueOrderTypes = [...new Set(sales.map(sale => sale.orderType))]
      const uniquePaymentMethods = [...new Set(sales.map(sale => sale.paymentMethod))]
      console.log('🔍 Unique orderType values in data:', uniqueOrderTypes)
      console.log('🔍 Unique paymentMethod values in data:', uniquePaymentMethods)
      
      // Store original count before filtering for comparison
      const ORIGINAL_COUNT = sales.length
      
      // Apply client-side filters based on the new filter types
      if (filters.search || filters.customerName || filters.customerSearch) {
        const searchTerm = (filters.search || filters.customerName || filters.customerSearch || '').toLowerCase().trim()
        console.log('🔍 Sales Summary Search Filter - Term:', searchTerm)
        
        sales = sales.filter(sale => {
          const customerName = (sale.customerName || '').toLowerCase()
          const orderId = (sale.id || '').toString().toLowerCase()
          const orderNumber = (sale.orderNumber || '').toString().toLowerCase()
          const paymentMethod = (sale.paymentMethod || '').toLowerCase()
          const orderType = (sale.orderType || '').toLowerCase()
          
          const matches = customerName.includes(searchTerm) ||
                         orderId.includes(searchTerm) ||
                         orderNumber.includes(searchTerm) ||
                         paymentMethod.includes(searchTerm) ||
                         orderType.includes(searchTerm)
          
          if (matches) {
            console.log('✅ Sales Summary Search Match:', { 
              customer: customerName,
              orderId: orderId,
              searchTerm 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Sales Summary Search Results: ${sales.length} sales after filtering`)
      }
      
      // Date filtering using fromDate/toDate instead of startDate/endDate
      if (filters.fromDate && filters.fromDate !== '') {
        console.log('🔍 Sales Summary From Date Filter - Date:', filters.fromDate)
        sales = sales.filter(sale => {
          const saleDate = new Date(sale.createdAt || sale.date)
          const fromDate = new Date(filters.fromDate)
          // Set from date to start of day
          fromDate.setHours(0, 0, 0, 0)
          const matches = saleDate >= fromDate
          
          if (matches) {
            console.log('✅ Sales Summary From Date Match:', { 
              saleDate: saleDate.toDateString(),
              fromDate: fromDate.toDateString()
            })
          }
          
          return matches
        })
        console.log(`📊 Sales Summary From Date Results: ${sales.length} sales after filtering`)
      }
      
      if (filters.toDate && filters.toDate !== '') {
        console.log('🔍 Sales Summary To Date Filter - Date:', filters.toDate)
        sales = sales.filter(sale => {
          const saleDate = new Date(sale.createdAt || sale.date)
          const toDate = new Date(filters.toDate)
          // Set to date to end of day
          toDate.setHours(23, 59, 59, 999)
          const matches = saleDate <= toDate
          
          if (matches) {
            console.log('✅ Sales Summary To Date Match:', { 
              saleDate: saleDate.toDateString(),
              toDate: toDate.toDateString()
            })
          }
          
          return matches
        })
        console.log(`📊 Sales Summary End Date Results: ${sales.length} sales after filtering`)
      }
      
      if (filters.orderType && filters.orderType !== '' && filters.orderType !== 'all') {
        console.log('🔍 Sales Summary Order Type Filter - Type:', filters.orderType)
        sales = sales.filter(sale => {
          // Map dropdown values to actual data values
          const filterToDataMap = {
            'dine_in': 'Dine In',
            'take_away': 'Take Away', 
            'delivery': 'Delivery'
          }
          
          // Get expected data value for this filter
          const expectedValue = filterToDataMap[filters.orderType] || filters.orderType
          const matches = sale.orderType === expectedValue
          
          if (matches) {
            console.log('✅ Sales Summary Order Type Match:', { 
              saleOrderType: sale.orderType,
              filterType: filters.orderType,
              expectedValue: expectedValue
            })
          } else {
            console.log('❌ Sales Summary Order Type No Match:', { 
              saleOrderType: sale.orderType,
              filterType: filters.orderType,
              expectedValue: expectedValue
            })
          }
          
          return matches
        })
        console.log(`📊 Sales Summary Order Type Results: ${sales.length} sales after filtering`)
      }
      
      if (filters.paymentMethod && filters.paymentMethod !== '' && filters.paymentMethod !== 'all') {
        console.log('🔍 Sales Summary Payment Method Filter - Method:', filters.paymentMethod)
        sales = sales.filter(sale => {
          const matches = sale.paymentMethod && 
                         sale.paymentMethod.toLowerCase() === filters.paymentMethod.toLowerCase()
          
          if (matches) {
            console.log('✅ Sales Summary Payment Method Match:', { 
              salePaymentMethod: sale.paymentMethod,
              filterMethod: filters.paymentMethod
            })
          } else {
            console.log('❌ Sales Summary Payment Method No Match:', { 
              salePaymentMethod: sale.paymentMethod,
              filterMethod: filters.paymentMethod
            })
          }
          
          return matches
        })
        console.log(`📊 Sales Summary Payment Method Results: ${sales.length} sales after filtering`)
      }
      
      if (filters.customer && filters.customer !== 'all') {
        sales = sales.filter(sale => {
          const customerId = sale.customer?._id
          return customerId === filters.customer
        })
      }
      
      // Calculate summary statistics
      const totalRevenue = sales.reduce((sum, sale) => sum + (parseFloat(sale.total) || 0), 0)
      const totalOrders = sales.length
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0
      
      const result = {
        sales: sales, // Return as 'sales' to match dataKey in component
        summary: {
          totalRevenue,
          totalOrders,
          avgOrderValue
        }
      }
      
      console.log('✅ Sales Summary Generated:', result.sales.length, 'records (from', ORIGINAL_COUNT, 'total), filters applied:', Object.keys(filters).length)
      return result
    } catch (error) {
      console.error('❌ Error generating sales summary report:', error)
      return { data: [], summary: { totalRevenue: 0, totalOrders: 0, avgOrderValue: 0, totalDiscount: 0 } }
    }
  }

  /**
   * Generate Payments Report - Individual Transactions
   */
  async generatePaymentsReport(filters = {}) {
    try {
      console.log('💳 Generating Payments Report with filters:', filters)
      
      const response = await SalesService.getAllSales({
        orderStatus: 'completed',
        ...filters
      })
      
      const parsedData = SalesService.parseResponse(response)
      let sales = parsedData.sales || []
      
      // Store original count for debugging
      const ORIGINAL_COUNT = sales.length
      console.log('🔍 Payments data received:', ORIGINAL_COUNT, 'records')
      
      // Debug: Show unique values in the data
      const uniqueOrderTypes = [...new Set(sales.map(sale => sale.orderType))]
      const uniquePaymentMethods = [...new Set(sales.map(sale => sale.orderPayment?.paymentMethods?.[0]?.method || 'cash'))]
      console.log('🔍 Unique orderType values in payments data:', uniqueOrderTypes)
      console.log('🔍 Unique paymentMethod values in payments data:', uniquePaymentMethods)

      // Apply client-side filters
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase().trim()
        console.log('🔍 Payments Search Filter - Term:', searchTerm)
        
        sales = sales.filter(sale => {
          const paymentMethod = (sale.orderPayment?.paymentMethods?.[0]?.method || '').toLowerCase()
          const customerName = (sale.customer?.name || '').toLowerCase()
          const orderId = (sale._id || sale.id || '').toString().toLowerCase()
          const orderNumber = (sale.orderNumber || '').toString().toLowerCase()
          
          const matches = paymentMethod.includes(searchTerm) ||
                         customerName.includes(searchTerm) ||
                         orderId.includes(searchTerm) ||
                         orderNumber.includes(searchTerm)
          
          if (matches) {
            console.log('✅ Payments Search Match:', { 
              paymentMethod: paymentMethod,
              customer: customerName,
              searchTerm 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Payments Search Results: ${sales.length} payments after filtering`)
      }
      
      // Date filtering using fromDate/toDate
      if (filters.fromDate && filters.fromDate !== '') {
        console.log('🔍 Payments From Date Filter - Date:', filters.fromDate)
        sales = sales.filter(sale => {
          const saleDate = new Date(sale.orderDate || sale.createdAt)
          const fromDate = new Date(filters.fromDate)
          fromDate.setHours(0, 0, 0, 0)
          const matches = saleDate >= fromDate
          
          if (matches) {
            console.log('✅ Payments From Date Match:', { 
              saleDate: saleDate.toDateString(),
              fromDate: fromDate.toDateString()
            })
          }
          
          return matches
        })
        console.log(`📊 Payments From Date Results: ${sales.length} payments after filtering`)
      }
      
      if (filters.toDate && filters.toDate !== '') {
        console.log('🔍 Payments To Date Filter - Date:', filters.toDate)
        sales = sales.filter(sale => {
          const saleDate = new Date(sale.orderDate || sale.createdAt)
          const toDate = new Date(filters.toDate)
          toDate.setHours(23, 59, 59, 999)
          const matches = saleDate <= toDate
          
          if (matches) {
            console.log('✅ Payments To Date Match:', { 
              saleDate: saleDate.toDateString(),
              toDate: toDate.toDateString()
            })
          }
          
          return matches
        })
        console.log(`📊 Payments To Date Results: ${sales.length} payments after filtering`)
      }
      
      // Order Type filtering  
      if (filters.orderType && filters.orderType !== '' && filters.orderType !== 'all') {
        console.log('🔍 Payments Order Type Filter - Type:', filters.orderType)
        sales = sales.filter(sale => {
          // Direct comparison: both filter and data use underscore format
          const matches = sale.orderType === filters.orderType
          
          if (matches) {
            console.log('✅ Payments Order Type Match:', { 
              saleOrderType: sale.orderType,
              filterType: filters.orderType
            })
          } else {
            console.log('❌ Payments Order Type No Match:', { 
              saleOrderType: sale.orderType,
              filterType: filters.orderType
            })
          }
          
          return matches
        })
        console.log(`📊 Payments Order Type Results: ${sales.length} payments after filtering`)
      }
      
      // Payment Method filtering
      if (filters.paymentMethod && filters.paymentMethod !== '' && filters.paymentMethod !== 'all') {
        console.log('🔍 Payments Payment Method Filter - Method:', filters.paymentMethod)
        sales = sales.filter(sale => {
          const paymentMethod = sale.orderPayment?.paymentMethods?.[0]?.method || 'cash'
          const matches = paymentMethod.toLowerCase() === filters.paymentMethod.toLowerCase()
          
          if (matches) {
            console.log('✅ Payments Payment Method Match:', { 
              salePaymentMethod: paymentMethod,
              filterMethod: filters.paymentMethod
            })
          } else {
            console.log('❌ Payments Payment Method No Match:', { 
              salePaymentMethod: paymentMethod,
              filterMethod: filters.paymentMethod
            })
          }
          
          return matches
        })
        console.log(`📊 Payments Payment Method Results: ${sales.length} payments after filtering`)
      }
      
      // Map each order to individual transaction format
      const paymentsData = sales.map(sale => {
        const orderDate = new Date(sale.orderDate || sale.createdAt)
        const paymentMethod = sale.orderPayment?.paymentMethods?.[0]?.method || 'cash'
        const totalAmount = sale.orderPayment?.totalAmount || 0
        const customerName = sale.customer?.name || 'Walking Customer'
        
        // Format order type
        let orderType = sale.orderType || 'dine_in'
        switch (orderType) {
          case 'dine_in':
            orderType = 'Dine In'
            break
          case 'take_away':
            orderType = 'Take Away'
            break
          case 'delivery':
            orderType = 'Delivery'
            break
          default:
            orderType = orderType.charAt(0).toUpperCase() + orderType.slice(1).replace('_', ' ')
        }
        
        // Format payment type
        let paymentType = paymentMethod
        switch (paymentMethod) {
          case 'cash':
            paymentType = 'Cash'
            break
          case 'card':
            paymentType = 'Card'
            break
          case 'redeem':
            paymentType = 'Points Redeem'
            break
          default:
            paymentType = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)
        }
        
        return {
          date: orderDate.toISOString().split('T')[0], // YYYY-MM-DD format
          orderNumber: sale.orderNumber || '',
          customer: customerName,
          orderType: orderType,
          totalBill: parseFloat(totalAmount) || 0,
          paymentType: paymentType
        }
      })
      
      const totalAmount = paymentsData.reduce((sum, p) => sum + p.totalBill, 0)
      const totalTransactions = paymentsData.length
      
      console.log('✅ Payments Report Generated:', paymentsData.length, 'individual transactions')
      
      return {
        data: paymentsData,
        summary: {
          totalAmount,
          totalTransactions,
          avgTransactionValue: totalTransactions > 0 ? totalAmount / totalTransactions : 0
        }
      }
    } catch (error) {
      console.error('❌ Error generating payments report:', error)
      return { data: [], summary: { totalAmount: 0, totalTransactions: 0, avgTransactionValue: 0 } }
    }
  }

  /**
   * Generate Product Summary Report
   */
  async generateProductSummaryReport(filters = {}) {
    try {
      console.log('📦 Generating Product Summary Report with filters:', filters)
      
      // Get product inventory data instead of just product data
      const response = await ProductInventoryService.getAllInventories()
      // ProductInventoryService returns {success, statusCode, message, data} where data contains productInventories array
      let inventories = response.data?.productInventories || []
      
      console.log('📦 Raw inventory data:', inventories.length, 'records')
      
      // Apply filters
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase().trim()
        console.log('🔍 Inventory Search Filter - Term:', searchTerm)
        
        inventories = inventories.filter(inventory => {
          const product = inventory.product || {}
          const branch = inventory.branch || {}
          
          const productName = (product.name || '').toLowerCase()
          const productCategory = typeof product.category === 'object' ? 
            (product.category?.name || '').toLowerCase() : 
            (product.category || '').toLowerCase()
          const productSKU = (product.sku || product.code || '').toLowerCase()
          const branchName = (branch.name || '').toLowerCase()
          
          const matches = productName.includes(searchTerm) ||
                         productCategory.includes(searchTerm) ||
                         productSKU.includes(searchTerm) ||
                         branchName.includes(searchTerm)
          
          if (matches) {
            console.log('✅ Inventory Search Match:', { 
              productName,
              productCategory,
              branchName,
              searchTerm 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Inventory Search Results: ${inventories.length} inventories after filtering`)
      }
      
      if (filters.branch && filters.branch !== 'all') {
        console.log('🔍 Branch Filter - Looking for branch:', filters.branch)
        
        const originalCount = inventories.length
        inventories = inventories.filter(inventory => {
          const branchId = inventory.branch?._id || inventory.branch?.id || inventory.branch
          const branchName = inventory.branch?.name || ''
          
          // Check both ID and name (case-insensitive for name, supports partial matches)
          const matchesId = branchId === filters.branch
          const matchesName = branchName.toLowerCase().includes(filters.branch.toLowerCase())
          const matches = matchesId || matchesName
          
          return matches
        })
        
        console.log(`📊 Branch Filter Results: ${inventories.length}/${originalCount} inventories after branch filtering`)
      }
      
      if (filters.category && filters.category !== 'all' && filters.category !== '') {
        console.log('🔍 Inventory Category Filter - Value:', filters.category)
        
        inventories = inventories.filter(inventory => {
          const product = inventory.product || {}
          const productCategoryId = product.category?._id || product.category?.id  
          const productCategoryName = typeof product.category === 'object' ? 
            (product.category?.name || '').toLowerCase() : 
            (product.category || '').toLowerCase()
          
          // Check both ID and name matching
          const matchesId = productCategoryId === filters.category
          const matchesName = productCategoryName.includes(filters.category.toLowerCase())
          const matches = matchesId || matchesName
          
          if (matches) {
            console.log('✅ Inventory Category Match:', { 
              productCategoryId,
              productCategoryName,
              categoryFilter: filters.category,
              productName: product.name
            })
          }
          
          return matches
        })
        
        console.log(`📊 Inventory Category Results: ${inventories.length} inventories after category filtering`)
      }
      
      if (filters.product && filters.product !== 'all') {
        inventories = inventories.filter(inventory => {
          const productId = inventory.product?._id || inventory.product?.id || inventory.product
          return productId === filters.product
        })
      }
      
      // Map inventories to new column structure based on actual inventory data
      const productsData = inventories.map(inventory => {
        const product = inventory.product || {}
        const branch = inventory.branch || {}
        
        return {
          code: product.sku || product.code || 'N/A',
          productName: product.name || 'Unknown Product',
          category: typeof product.category === 'object' ? 
            product.category?.name || 'Uncategorized' : 
            product.category || 'Uncategorized',
          currentStock: Number(inventory.currentStock) || 0, // Real current stock from inventory
          purchasePrice: parseFloat(product.cost) || 0,
          salesPrice: parseFloat(product.outletPrice) || parseFloat(product.sellingPrice) || 0,
          branchName: branch.name || 'Unknown Branch' // Real branch name from inventory
        }
      })
      
      const totalProducts = productsData.length
      const totalPurchaseValue = productsData.reduce((sum, p) => sum + (p.currentStock * p.purchasePrice), 0)
      const totalSalesValue = productsData.reduce((sum, p) => sum + (p.currentStock * p.salesPrice), 0)
      const avgPurchasePrice = productsData.length > 0 ? 
        productsData.reduce((sum, p) => sum + p.purchasePrice, 0) / productsData.length : 0
      const avgSalesPrice = productsData.length > 0 ? 
        productsData.reduce((sum, p) => sum + p.salesPrice, 0) / productsData.length : 0
      
      console.log('✅ Product Summary Generated from Inventory:', productsData.length, 'products')
      
      return {
        data: productsData,
        summary: {
          totalProducts,
          totalPurchaseValue,
          totalSalesValue,
          avgPurchasePrice,
          avgSalesPrice
        }
      }
    } catch (error) {
      console.error('❌ Error generating product summary report:', error)
      return { 
        data: [], 
        summary: { 
          totalProducts: 0, 
          totalPurchaseValue: 0, 
          totalSalesValue: 0, 
          avgPurchasePrice: 0, 
          avgSalesPrice: 0 
        } 
      }
    }
  }

  /**
   * Generate Stock Alerts Report
   */
  async generateStockAlertsReport(filters = {}) {
    try {
      console.log('⚠️ Generating Stock Alerts Report with filters:', filters)
      
      // Mock data for now - will be replaced with actual stock alerts endpoint
      let mockData = [
        {
          itemId: 'PR001',
          itemName: 'Cappuccino',
          itemType: 'product',
          currentStock: 5,
          threshold: 10,
          branchName: 'Tezlaa Colombo',
          branchId: '68ab4a5075017ad6d7d8ad53',
          status: 'low'
        },
        {
          itemId: 'RM001',
          itemName: 'Coffee Beans',
          itemType: 'raw_material',
          currentStock: 0,
          threshold: 20,
          branchName: 'Tezlaa Colombo',
          branchId: '68ab4a5075017ad6d7d8ad53',
          status: 'out_of_stock'
        },
        {
          itemId: 'PR002',
          itemName: 'Croissant',
          itemType: 'product',
          currentStock: 3,
          threshold: 15,
          branchName: 'Tezlaa Negombo',
          branchId: '68ab4a8275017ad6d7d8ad5b',
          status: 'critical'
        },
        {
          itemId: 'RM002',
          itemName: 'Milk',
          itemType: 'raw_material',
          currentStock: 8,
          threshold: 25,
          branchName: 'Tezlaa Negombo',
          branchId: '68ab4a8275017ad6d7d8ad5b',
          status: 'low'
        },
        {
          itemId: 'PR003',
          itemName: 'Sandwich',
          itemType: 'product',
          currentStock: 2,
          threshold: 10,
          branchName: 'Tezlaa Colombo',
          branchId: '68ab4a5075017ad6d7d8ad53',
          status: 'critical'
        },
        {
          itemId: 'RM003',
          itemName: 'Sugar',
          itemType: 'raw_material',
          currentStock: 0,
          threshold: 30,
          branchName: 'Tezlaa Negombo',
          branchId: '68ab4a8275017ad6d7d8ad5b',
          status: 'out_of_stock'
        }
      ]

      console.log('📊 Total stock alerts before filtering:', mockData.length)
      
      // Apply status filter
      if (filters.status && filters.status !== 'all' && filters.status !== '') {
        console.log('🔍 Stock Alerts Status Filter - Status:', filters.status)
        const beforeStatusFilter = mockData.length
        mockData = mockData.filter(item => item.status === filters.status)
        console.log(`📊 Stock Alerts Status Filter Results: ${mockData.length}/${beforeStatusFilter} items after filtering`)
      }
      
      // Apply branch filter
      if (filters.branch && filters.branch !== 'all' && filters.branch !== '') {
        console.log('🔍 Stock Alerts Branch Filter - Branch ID:', filters.branch)
        const beforeBranchFilter = mockData.length
        mockData = mockData.filter(item => item.branchId === filters.branch)
        console.log(`📊 Stock Alerts Branch Filter Results: ${mockData.length}/${beforeBranchFilter} items after filtering`)
      }
      
      // Apply item type filter
      if (filters.itemType && filters.itemType !== 'all' && filters.itemType !== '') {
        console.log('🔍 Stock Alerts Item Type Filter - Type:', filters.itemType)
        const beforeTypeFilter = mockData.length
        mockData = mockData.filter(item => item.itemType === filters.itemType)
        console.log(`📊 Stock Alerts Item Type Filter Results: ${mockData.length}/${beforeTypeFilter} items after filtering`)
      }
      
      // Calculate summary statistics
      const outOfStock = mockData.filter(item => item.status === 'out_of_stock').length
      const critical = mockData.filter(item => item.status === 'critical').length
      const lowStock = mockData.filter(item => item.status === 'low').length
      const totalItems = mockData.length
      
      console.log('✅ Stock Alerts Generated:', mockData.length, 'alerts')
      
      return {
        success: true,
        data: mockData,
        summary: {
          totalItems,
          outOfStock,
          critical,
          lowStock,
          byType: {
            products: mockData.filter(item => item.itemType === 'product').length,
            rawMaterials: mockData.filter(item => item.itemType === 'raw_material').length
          }
        }
      }
    } catch (error) {
      console.error('❌ Error generating stock alerts report:', error)
      return { 
        success: false, 
        error: error.message,
        data: [],
        summary: { 
          totalItems: 0, 
          outOfStock: 0, 
          critical: 0, 
          lowStock: 0,
          byType: { products: 0, rawMaterials: 0 }
        } 
      }
    }
  }

  /**
   * Generate Customers Report  
   */
  async generateCustomersReport(filters = {}) {
    try {
      console.log('👥 Generating Customers Report with filters:', filters)
      
      // Mock data for now - would replace with actual API call
      let customers = [
        {
          _id: 'CUST001',
          name: 'John Doe',
          mobileNumber: '+94712345678',
          pointsBalance: 250,
          redeemedPoints: 50,
          totalOrders: 15,
          totalSpent: 12500,
          branch: { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo' },
          isActive: true
        },
        {
          _id: 'CUST002',
          name: 'Jane Smith', 
          mobileNumber: '+94723456789',
          pointsBalance: 180,
          redeemedPoints: 20,
          totalOrders: 8,
          totalSpent: 8200,
          branch: { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo' },
          isActive: true
        },
        {
          _id: 'CUST003',
          name: 'Michael Johnson',
          mobileNumber: '+94734567890',
          pointsBalance: 320,
          redeemedPoints: 80,
          totalOrders: 22,
          totalSpent: 18750,
          branch: { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo' },
          isActive: true
        },
        {
          _id: 'CUST004',
          name: 'Sarah Williams',
          mobileNumber: '+94745678901',
          pointsBalance: 95,
          redeemedPoints: 15,
          totalOrders: 5,
          totalSpent: 4200,
          branch: { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo' },
          isActive: true
        },
        {
          _id: 'CUST005',
          name: 'David Brown',
          mobileNumber: '+94756789012',
          pointsBalance: 420,
          redeemedPoints: 130,
          totalOrders: 35,
          totalSpent: 28900,
          branch: { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo' },
          isActive: true
        },
        {
          _id: 'CUST006',
          name: 'Emily Davis',
          mobileNumber: '+94767890123',
          pointsBalance: 65,
          redeemedPoints: 10,
          totalOrders: 3,
          totalSpent: 2150,
          branch: { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo' },
          isActive: false
        }
      ]

      // Try to get real data first, fallback to mock data if needed
      try {
        const response = await CustomerService.getAllCustomers()
        console.log('📊 Customer API Response:', response)
        
        if (response && response.success && response.data && response.data.customers) {
          customers = response.data.customers
          console.log('📊 Using real customer data:', customers.length, 'customers')
        } else {
          console.log('📊 Using mock customer data - API response not in expected format')
        }
      } catch (error) {
        console.log('📊 Using mock customer data due to API error:', error.message)
      }
      
      console.log('🔍 Customer data structure (first customer):', customers[0])
      console.log('📊 Total customers before filtering:', customers.length)
      
      // Apply filters
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase()
        customers = customers.filter(customer => 
          (customer._id || customer.id || '').toLowerCase().includes(searchTerm) ||
          (customer.name || '').toLowerCase().includes(searchTerm) ||
          (customer.mobileNumber || customer.mobile || customer.phone || '').toLowerCase().includes(searchTerm)
        )
        console.log('🔍 After search filter:', customers.length, 'customers')
      }
      
      if (filters.branch && filters.branch !== 'all') {
        console.log('🏢 Filtering by branch:', filters.branch)
        console.log('🏢 Sample customer branch data:', customers[0]?.branch)
        
        const beforeFilter = customers.length
        customers = customers.filter(customer => {
          // Try multiple branch matching strategies
          const branchId = customer.branch?._id || customer.branchId || customer.branch_id
          const branchName = customer.branch?.name || customer.branchName || customer.branch_name
          
          // For debugging - log each customer's branch info
          if (customers.indexOf(customer) < 3) { // Only log first 3 customers
            console.log('🏢 Customer branch info:', { 
              customerName: customer.name,
              branchId, 
              branchName, 
              filterValue: filters.branch,
              fullBranch: customer.branch
            })
          }
          
          // Match by ID or name (case insensitive)
          const matchById = branchId === filters.branch
          const matchByName = branchName && branchName.toLowerCase() === filters.branch.toLowerCase()
          
          // Also try matching if the branch name contains the filter value (partial match)
          const partialNameMatch = branchName && branchName.toLowerCase().includes(filters.branch.toLowerCase())
          
          return matchById || matchByName || partialNameMatch
        })
        console.log('🔍 After branch filter:', customers.length, 'customers (was', beforeFilter, ')')
      }
      
      const customerStats = customers.map(customer => {
        // Calculate estimated orders and purchases based on loyalty points
        // This is an approximation since the API doesn't provide order history
        const loyaltyPoints = parseFloat(customer.lifetimePointsEarned || customer.pointsBalance || customer.points || 0)
        const estimatedTotalPurchase = loyaltyPoints * 10 // Assuming 1 point per 10 rupees spent
        const estimatedTotalOrders = Math.max(1, Math.floor(loyaltyPoints / 20)) // Assuming ~20 points per order
        
        return {
          customerId: customer._id || customer.id || 'N/A',
          customerName: customer.name || 'Unknown Customer',
          mobileNumber: customer.mobileNumber || customer.mobile || customer.phone || 'N/A',
          loyaltyPoints: parseFloat(loyaltyPoints.toFixed(2)),
          redeemPoints: parseFloat((customer.lifetimePointsRedeemed || customer.redeemedPoints || customer.usedPoints || 0).toFixed(2)),
          remainingPoints: parseFloat((customer.pointsBalance || customer.points || 0).toFixed(2)),
          totalOrders: parseInt(customer.totalOrders || customer.orderCount || estimatedTotalOrders),
          totalPurchase: parseFloat((customer.totalSpent || customer.totalPurchase || customer.totalAmount || estimatedTotalPurchase).toFixed(2)),
          branch: customer.branch?.name || customer.branchName || 'Unknown Branch'
        }
      })
      
      const totalCustomers = customerStats.length
      const totalLoyaltyPoints = customerStats.reduce((sum, c) => sum + c.loyaltyPoints, 0)
      const totalRedeemPoints = customerStats.reduce((sum, c) => sum + c.redeemPoints, 0)
      const totalRemainingPoints = customerStats.reduce((sum, c) => sum + c.remainingPoints, 0)
      const totalOrders = customerStats.reduce((sum, c) => sum + c.totalOrders, 0)
      const totalPurchases = customerStats.reduce((sum, c) => sum + c.totalPurchase, 0)
      
      console.log('✅ Customers Report Generated:', customerStats.length, 'customers')
      
      return {
        success: true,
        data: customerStats,
        summary: {
          totalCustomers,
          totalLoyaltyPoints,
          totalRedeemPoints,
          totalRemainingPoints,
          totalOrders,
          totalPurchases,
          avgOrderValue: totalOrders > 0 ? totalPurchases / totalOrders : 0,
          avgLoyaltyPoints: totalCustomers > 0 ? totalLoyaltyPoints / totalCustomers : 0
        }
      }
    } catch (error) {
      console.error('❌ Error generating customers report:', error)
      return { data: [], summary: { totalCustomers: 0, activeCustomers: 0, loyaltyMembers: 0, avgLoyaltyPoints: 0 } }
    }
  }

  /**
   * Generate Employees Report
   */
  async generateEmployeesReport(filters = {}) {
    try {
      console.log('👨‍💼 Generating Employees Report with filters:', filters)
      
      // Mock data for demonstration - would replace with actual API call
      let employees = [
        {
          _id: 'EMP001',
          name: 'John Manager',
          fullName: 'John Manager',
          job: { name: 'Manager' },
          basicSalary: 85000,
          noOfLeaves: 5,
          branch: { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo' },
          dateOfJoining: '2023-01-15',
          isActive: true
        },
        {
          _id: 'EMP002',
          name: 'Sarah Barista',
          fullName: 'Sarah Barista',
          job: { name: 'Barista' },
          basicSalary: 45000,
          noOfLeaves: 2,
          branch: { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo' },
          dateOfJoining: '2023-03-20',
          isActive: true
        },
        {
          _id: 'EMP003',
          name: 'Mike Cashier',
          fullName: 'Mike Cashier',
          job: { name: 'Cashier' },
          basicSalary: 40000,
          noOfLeaves: 1,
          branch: { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo' },
          dateOfJoining: '2023-05-10',
          isActive: true
        },
        {
          _id: 'EMP004',
          name: 'Lisa Supervisor',
          fullName: 'Lisa Supervisor',
          job: { name: 'Supervisor' },
          basicSalary: 65000,
          noOfLeaves: 3,
          branch: { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo' },
          dateOfJoining: '2022-11-05',
          isActive: true
        },
        {
          _id: 'EMP005',
          name: 'Tom Chef',
          fullName: 'Tom Chef',
          job: { name: 'Chef' },
          basicSalary: 55000,
          noOfLeaves: 0,
          branch: { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo' },
          dateOfJoining: '2023-02-28',
          isActive: true
        },
        {
          _id: 'EMP006',
          name: 'Emma Waiter',
          fullName: 'Emma Waiter',
          job: { name: 'Waiter' },
          basicSalary: 38000,
          noOfLeaves: 4,
          branch: { _id: '68ab4ac575017ad6d7d8ad63', name: 'Tezlaa Warehouse' },
          dateOfJoining: '2023-08-12',
          isActive: false
        }
      ]

      // Try to get real data first, fallback to mock data if needed
      try {
        const response = await employeeService.getAllEmployees()
        console.log('📊 Employee API Response:', response)
        
        if (response && response.success && response.data) {
          if (response.data.employees && Array.isArray(response.data.employees)) {
            employees = response.data.employees
            console.log('📊 Using real employee data:', employees.length, 'employees')
          } else if (Array.isArray(response.data)) {
            employees = response.data
            console.log('📊 Using real employee data:', employees.length, 'employees')
          } else {
            console.log('📊 Using mock employee data - API response not in expected format')
          }
        } else {
          console.log('📊 Using mock employee data - API response unsuccessful')
        }
      } catch (error) {
        console.log('📊 Using mock employee data due to API error:', error.message)
      }
      
      // Debug: Log the first employee to see available fields
      if (employees.length > 0) {
        console.log('🔍 First employee data structure:', employees[0])
      }
      
      // Apply filters
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase().trim()
        console.log('🔍 Employee Search Filter - Term:', searchTerm)
        
        employees = employees.filter(employee => {
          const employeeId = (employee._id || employee.id || employee.employeeId || '').toLowerCase()
          const employeeName = (employee.name || employee.firstName || employee.fullName || employee.employee_name || '').toLowerCase()
          const employeeEmail = (employee.email || employee.emailAddress || employee.work_email || '').toLowerCase()  
          const employeePosition = (employee.position || employee.role || employee.jobTitle || employee.designation || '').toLowerCase()
          const employeeDepartment = (employee.department || employee.dept || employee.division || '').toLowerCase()
          const employeePhone = (employee.phone || employee.phoneNumber || employee.mobile || employee.contact || '').toLowerCase()
          
          const matches = employeeId.includes(searchTerm) ||
                         employeeName.includes(searchTerm) ||
                         employeeEmail.includes(searchTerm) ||
                         employeePosition.includes(searchTerm) ||
                         employeeDepartment.includes(searchTerm) ||
                         employeePhone.includes(searchTerm)
          
          if (matches) {
            console.log('✅ Employee Search Match:', { 
              name: employeeName,
              email: employeeEmail,
              position: employeePosition,
              searchTerm 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Employee Search Results: ${employees.length} employees after filtering`)
      }
      
      if (filters.branch && filters.branch !== 'all') {
        console.log('🔍 Employee Branch Filter - Term:', filters.branch)
        console.log('🔍 Total employees before branch filtering:', employees.length)
        
        // Debug: Log branch-related fields for the first few employees
        employees.slice(0, 3).forEach((employee, index) => {
          console.log(`🔍 Employee ${index + 1} branch fields:`, {
            branchId: employee.branchId,
            branch_id: employee.branch_id,
            'branch?._id': employee.branch?._id,
            branch: employee.branch,
            location: employee.location,
            office: employee.office,
            workplace: employee.workplace,
            fullName: employee.fullName || employee.name
          })
        })
        
        employees = employees.filter(employee => {
          const employeeBranchName = employee.branch?.name || employee.branch_name || employee.branchName
          const filterValue = filters.branch.toLowerCase()
          const branchNameLower = (employeeBranchName || '').toLowerCase()
          
          // Simple exact and partial matching
          let matches = false
          
          if (branchNameLower === filterValue) {
            matches = true
          } else if (branchNameLower.includes(filterValue)) {
            matches = true
          } else if (filterValue.includes(branchNameLower.split(' ').pop()?.toLowerCase() || '')) {
            matches = true
          }
          
          // Special cases for the known branches
          if (filterValue === 'colombo' && branchNameLower.includes('colombo')) {
            matches = true
          } else if (filterValue === 'negombo' && branchNameLower.includes('negombo')) {
            matches = true
          } else if (filterValue === 'warehouse' && branchNameLower.includes('warehouse')) {
            matches = true
          }
          
          if (matches) {
            console.log('✅ Employee Branch Match:', { 
              employeeName: employee.fullName || employee.name,
              branchName: employeeBranchName,
              filterBranch: filters.branch,
              match: 'found'
            })
          }
          
          return matches
        })
        
        console.log(`📊 Employee Branch Results: ${employees.length} employees after branch filtering`)
      }
      
      if (filters.job && filters.job !== 'all') {
        console.log('🔍 Employee Job Filter - Term:', filters.job)
        
        employees = employees.filter(employee => {
          const employeeJobName = (employee.job?.name || '').toLowerCase()
          const employeePosition = (employee.position || employee.role || employee.designation || '').toLowerCase()
          const filterJob = filters.job.toLowerCase()
          
          const matches = employeeJobName === filterJob || 
                         employeePosition === filterJob ||
                         employeeJobName.includes(filterJob) ||
                         employeePosition.includes(filterJob)
          
          if (matches) {
            console.log('✅ Employee Job Match:', { 
              employeeName: employee.fullName || employee.name,
              jobName: employeeJobName,
              position: employeePosition,
              filterJob: filterJob
            })
          }
          
          return matches
        })
        
        console.log(`📊 Employee Job Results: ${employees.length} employees after job filtering`)
      }
      
      const employeesData = employees.map(employee => ({
        employeeId: employee._id || employee.id || employee.employeeId,
        employeeName: employee.fullName || employee.name || employee.firstName || employee.employee_name || 'Unknown Employee',
        designation: employee.job?.name || employee.position || employee.role || employee.jobTitle || 'Staff',
        basicSalary: parseFloat(employee.basicSalary || employee.salary || employee.wage || 0),
        leaves: parseInt(employee.noOfLeaves || employee.remainingLeaves || employee.totalLeaves || 0),
        branch: employee.branch?.name || employee.branch_name || employee.branchName || employee.location || 'Main',
        joinDate: employee.dateOfJoining || employee.hire_date || employee.hireDate || employee.joinDate || employee.created_at || employee.createdAt || 'Unknown',
        // Keep additional fields for compatibility
        department: employee.branch?.name || employee.department || employee.dept || employee.division || 'General',
        email: employee.emailAddress || employee.email || employee.work_email || 'N/A',
        phone: employee.contactNumber || employee.phone || employee.phoneNumber || employee.mobile || employee.contact || 'N/A',
        salary: parseFloat(employee.basicSalary || employee.salary || employee.wage || 0),
        hireDate: employee.dateOfJoining || employee.hire_date || employee.hireDate || employee.joinDate || employee.created_at || employee.createdAt || 'Unknown',
        status: employee.isActive !== undefined ? (employee.isActive ? 'Active' : 'Inactive') : 'Active',
        position: employee.job?.name || employee.position || employee.role || employee.jobTitle || 'Staff'
      }))
      
      const totalEmployees = employeesData.length
      const activeEmployees = employeesData.filter(emp => emp.status === 'Active').length
      const totalSalary = employeesData.reduce((sum, emp) => sum + emp.salary, 0)
      const avgSalary = totalEmployees > 0 ? totalSalary / totalEmployees : 0
      
      console.log('✅ Employees Report Generated:', employeesData.length, 'employees')
      
      return {
        success: true,
        data: employeesData,
        summary: {
          totalEmployees,
          activeEmployees,
          totalSalary: parseFloat(totalSalary.toFixed(2)),
          avgSalary: parseFloat(avgSalary.toFixed(2))
        }
      }
    } catch (error) {
      console.error('❌ Error generating employees report:', error)
      return { data: [], summary: { totalEmployees: 0, activeEmployees: 0, totalSalary: 0, avgSalary: 0 } }
    }
  }

  /**
   * Generate Daily Sales Report
   */
  async generateDailySalesReport(filters = {}) {
    try {
      console.log('📅 Generating Daily Sales Report')
      
      const response = await SalesService.getAllSales({
        orderStatus: 'completed',
        ...filters
      })
      
      const parsedData = SalesService.parseResponse(response)
      const sales = parsedData.sales || []
      
      // Group sales by date
      const dailyGroups = sales.reduce((acc, sale) => {
        const formatted = SalesModel.formatSaleForDisplay(sale)
        const date = formatted.orderDateFormatted || new Date().toLocaleDateString()
        
        if (!acc[date]) {
          acc[date] = { revenue: 0, orders: 0 }
        }
        
        acc[date].revenue += parseFloat(formatted.total) || 0
        acc[date].orders++
        
        return acc
      }, {})
      
      // Convert to array format
      const dailyData = Object.entries(dailyGroups).map(([date, data]) => ({
        date,
        totalOrders: data.orders,
        totalRevenue: data.revenue,
        avgOrderValue: data.orders > 0 ? data.revenue / data.orders : 0
      }))
      
      // Sort by date (descending)
      dailyData.sort((a, b) => new Date(b.date) - new Date(a.date))
      
      const totalRevenue = dailyData.reduce((sum, day) => sum + day.totalRevenue, 0)
      const totalOrders = dailyData.reduce((sum, day) => sum + day.totalOrders, 0)
      
      console.log('✅ Daily Sales Report Generated:', dailyData.length, 'days')
      
      return {
        data: dailyData,
        summary: {
          totalDays: dailyData.length,
          totalRevenue,
          totalOrders,
          avgDailyRevenue: dailyData.length > 0 ? totalRevenue / dailyData.length : 0
        }
      }
    } catch (error) {
      console.error('❌ Error generating daily sales report:', error)
      return { data: [], summary: { totalDays: 0, totalRevenue: 0, totalOrders: 0, avgDailyRevenue: 0 } }
    }
  }

  /**
   * Generate report based on type
   */
  async generateReport(reportType, filters = {}) {
    console.log(`🔄 Generating report: ${reportType}`)
    
    switch (reportType) {
      case 'sales-summary':
        return this.generateSalesSummaryReport(filters)
      case 'payments':
        return this.generatePaymentsReport(filters)
      case 'product-summary':
        return this.generateProductSummaryReport(filters)
      case 'stock-alerts':
      case 'stockAlerts':
        return this.generateStockAlertsReport(filters)
      case 'customers':
        return this.generateCustomersReport(filters)
      case 'employees':
        return this.generateEmployeesReport(filters)
      case 'daily-sales':
        return this.generateDailySalesReport(filters)
      case 'raw-material-summary':
      case 'rawMaterialSummary':
        return this.generateRawMaterialSummaryReport(filters)
      case 'productMargin':
      case 'product-margin':
        return this.generateProductMarginReport(filters)
      case 'suppliers':
        return this.generateSuppliersReport(filters)
      case 'salaryReports':
      case 'salary-reports':
        return this.generateSalaryReports(filters)
      case 'customerReturns':
      case 'customer-returns':
        return this.generateCustomerReturnsReport(filters)
      case 'leaveReports':
      case 'leave-reports':
        return this.generateLeaveReports(filters)
      case 'supplierReturns':
      case 'supplier-returns':
        return this.generateSupplierReturnsReport(filters)
      default:
        console.warn(`⚠️ Report type '${reportType}' not implemented yet`)
        return { data: [], summary: {} }
    }
  }

  /**
   * Generate Raw Material Summary Report
   */
  async generateRawMaterialSummaryReport(filters = {}) {
    try {
      console.log('📊 Generating Raw Material Summary Report with filters:', filters)
      
      // Get raw material inventory data from the API
      const response = await RawMaterialInventoryService.getAllInventories(filters)
      console.log('📦 Raw material inventory API response:', response)
      
      // Extract data from response
      let rawMaterials = []
      if (response.success) {
        if (response.data && response.data.rawMaterialInventories && Array.isArray(response.data.rawMaterialInventories)) {
          rawMaterials = response.data.rawMaterialInventories
        } else if (Array.isArray(response.data)) {
          rawMaterials = response.data
        }
      }
      
      console.log(`📦 Extracted ${rawMaterials.length} raw material inventory records`)
      
      if (rawMaterials.length === 0) {
        console.warn('⚠️ No raw material inventory data found, using mock data')
        
        // Create mock data with the actual branch ID being filtered if available
        const mockBranchId1 = filters.branch || "68ab4ac575017ad6d7d8ad63"
        const mockBranchId2 = "68ab4ac575017ad6d7d8ad64" 
        const mockBranchId3 = "68ab4ac575017ad6d7d8ad65"
        
        // Use mock data as fallback
        rawMaterials = [
          {
            id: 1,
            code: "RM001",
            rawMaterialName: "Coffee Beans",
            materialName: "Coffee Beans",
            category: "Beverages",
            currentStock: 50,
            currentStockWithUnit: "50 kg",
            unit: "kg",
            purchasePrice: 250,
            totalValue: 12500,
            supplier: "ABC Coffee Suppliers",
            branch: { _id: mockBranchId1, name: "Tezlaa Colombo" },
            branchName: "Tezlaa Colombo",
            lastUpdated: new Date().toISOString()
          },
          {
            id: 2,
            code: "RM002",
            rawMaterialName: "Milk",
            materialName: "Milk",
            category: "Dairy",
            currentStock: 100,
            currentStockWithUnit: "100 liter",
            unit: "liter",
            purchasePrice: 120,
            totalValue: 12000,
            supplier: "Fresh Dairy Ltd",
            branch: { _id: mockBranchId1, name: "Tezlaa Colombo" },
            branchName: "Tezlaa Colombo",
            lastUpdated: new Date().toISOString()
          },
          {
            id: 3,
            code: "RM003",
            rawMaterialName: "Sugar",
            materialName: "Sugar",
            category: "Sweeteners",
            currentStock: 75,
            currentStockWithUnit: "75 kg",
            unit: "kg",
            purchasePrice: 80,
            totalValue: 6000,
            supplier: "Sweet Supplies Inc",
            branch: { _id: mockBranchId2, name: "Tezlaa Negombo" },
            branchName: "Tezlaa Negombo",
            lastUpdated: new Date().toISOString()
          },
          {
            id: 4,
            code: "RM004",
            rawMaterialName: "Cocoa Powder",
            materialName: "Cocoa Powder",
            category: "Beverages",
            currentStock: 30,
            currentStockWithUnit: "30 kg",
            unit: "kg",
            purchasePrice: 300,
            totalValue: 9000,
            supplier: "Chocolate World",
            branch: { _id: mockBranchId2, name: "Tezlaa Negombo" },
            branchName: "Tezlaa Negombo",
            lastUpdated: new Date().toISOString()
          },
          {
            id: 5,
            code: "RM005",
            rawMaterialName: "Tea Leaves",
            materialName: "Tea Leaves",
            category: "Beverages",
            currentStock: 40,
            currentStockWithUnit: "40 kg",
            unit: "kg",
            purchasePrice: 200,
            totalValue: 8000,
            supplier: "Ceylon Tea Exports",
            branch: { _id: mockBranchId3, name: "Tezlaa Warehouse" },
            branchName: "Tezlaa Warehouse",
            lastUpdated: new Date().toISOString()
          }
        ]
      }
      
      // Transform data to ensure it has the required fields
      const transformedData = rawMaterials.map(item => {
        const rawMaterial = item.rawMaterial || {}
        const branch = item.branch || {}
        const unit = item.unit || rawMaterial.unit || {}
        
        // Calculate total value if not provided
        const currentStock = parseFloat(item.currentStock || item.quantity || 0)
        const purchasePrice = parseFloat(item.purchasePrice || rawMaterial.purchasePrice || 0)
        const totalValue = item.totalValue || (currentStock * purchasePrice)
        
        // Extract unit name or code if it's an object
        let unitDisplay = 'pcs'
        if (typeof unit === 'object' && unit !== null) {
          unitDisplay = unit.abbreviation || unit.name || unit.code || 'pcs'
        } else if (typeof unit === 'string') {
          unitDisplay = unit
        }
        
        // Extract branch name
        let branchName = 'Main Branch'
        if (typeof branch === 'object' && branch !== null) {
          branchName = branch.name || 'Main Branch'
        } else if (typeof branch === 'string') {
          branchName = branch
        }
        
        // Create current stock with unit display
        const stockValue = currentStock === 0 ? '0' : parseFloat(currentStock).toLocaleString()
        const currentStockWithUnit = unitDisplay ? `${stockValue} ${unitDisplay}` : stockValue
        
        return {
          id: item._id || item.id || '',
          code: item.code || rawMaterial.code || `RM${Math.floor(Math.random() * 1000)}`,
          rawMaterialName: rawMaterial.name || item.rawMaterialName || item.name || 'Unknown Material',
          materialName: rawMaterial.name || item.rawMaterialName || item.name || 'Unknown Material',
          category: rawMaterial.category?.name || item.category || 'Uncategorized',
          currentStock: currentStock,
          currentStockWithUnit: currentStockWithUnit,
          unit: unitDisplay,
          purchasePrice: purchasePrice,
          totalValue: totalValue,
          supplier: typeof item.supplier === 'object' ? item.supplier?.name || 'N/A' : item.supplier || 'N/A',
          branch: item.branch || branch, // Preserve the branch object
          branchName: branchName,
          lastUpdated: item.updatedAt || item.lastUpdated || new Date().toISOString()
        }
      })
      
      // Apply branch filter
      let filteredData = [...transformedData]
      
      if (filters.branch && filters.branch !== 'all' && filters.branch !== '') {
        console.log('🔍 Raw Material Branch Filter - Branch ID:', filters.branch)
        console.log('🔍 Data before filtering:', filteredData.length, 'items')
        console.log('🔍 Sample item structure:', filteredData[0])
        
        filteredData = filteredData.filter(item => {
          // Get the branch id from the item
          const branchId = item.branch?._id || item.branch?.id || null
          const branchName = item.branchName || ''
          
          console.log('🔍 Checking item:', {
            itemBranchId: branchId,
            itemBranchName: branchName,
            filterBranchId: filters.branch,
            materialName: item.rawMaterialName
          })
          
          // Check both ID match and name match (for flexibility)
          const matchesId = branchId === filters.branch
          const matchesName = branchName.toLowerCase().includes(filters.branch.toLowerCase()) ||
                             filters.branch.toLowerCase().includes(branchName.toLowerCase())
          
          const matches = matchesId || matchesName
          
          console.log('🔍 Branch filter result:', matches, 'for item:', item.rawMaterialName)
          
          return matches
        })
        
        console.log(`📊 Raw Material Branch Filter Results: ${filteredData.length} items after filtering`)
        console.log('📊 Filtered items:', filteredData.map(item => `${item.rawMaterialName} (${item.branchName})`))
      }
      
      const totalItems = filteredData.length
      const totalStock = filteredData.reduce((sum, item) => sum + item.currentStock, 0)
      const totalValue = filteredData.reduce((sum, item) => sum + (item.totalValue || 0), 0)
      
      return {
        success: true,
        data: filteredData,
        summary: {
          totalItems,
          totalStock,
          totalValue,
          avgValue: totalItems > 0 ? totalValue / totalItems : 0
        }
      }
    } catch (error) {
      console.error('❌ Error generating raw material summary report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Product Margin Report
   */
  async generateProductMarginReport(filters = {}) {
    try {
      console.log('📊 Generating Product Margin Report with filters:', filters)
      
      // Static mock data with realistic category and branch distribution
      let mockData = [
        {
          id: 1,
          code: 'PR001',
          productName: 'Cappuccino',
          category: 'Hot Beverages',
          sellingPrice: 120,
          purchasePrice: 80,
          margin: 40,
          branchId: '68ab4a5075017ad6d7d8ad53',
          branchName: 'Tezlaa Colombo',
          categoryId: '68ab4bbe75017ad6d7d8adf2' // Hot Beverages
        },
        {
          id: 2,
          code: 'PR002',
          productName: 'Chocolate Cake',
          category: 'Desserts',
          sellingPrice: 200,
          purchasePrice: 120,
          margin: 80,
          branchId: '68ab4a5075017ad6d7d8ad53',
          branchName: 'Tezlaa Colombo',
          categoryId: '68ab4b9675017ad6d7d8adbd' // Desserts
        },
        {
          id: 3,
          code: 'PR003',
          productName: 'Latte',
          category: 'Hot Beverages',
          sellingPrice: 110,
          purchasePrice: 75,
          margin: 35,
          branchId: '68ab4a8275017ad6d7d8ad5b',
          branchName: 'Tezlaa Negombo',
          categoryId: '68ab4bbe75017ad6d7d8adf2' // Hot Beverages
        },
        {
          id: 4,
          code: 'PR004',
          productName: 'Sandwich',
          category: 'Food',
          sellingPrice: 150,
          purchasePrice: 90,
          margin: 60,
          branchId: '68ab4a8275017ad6d7d8ad5b',
          branchName: 'Tezlaa Negombo',
          categoryId: '68ab4b8975017ad6d7d8adb8' // Food
        },
        {
          id: 5,
          code: 'PR005',
          productName: 'Iced Coffee',
          category: 'Cold Beverages',
          sellingPrice: 130,
          purchasePrice: 85,
          margin: 45,
          branchId: '68ab4a5075017ad6d7d8ad53',
          branchName: 'Tezlaa Colombo',
          categoryId: '68ab4b8975017ad6d7d8adb7' // Cold Beverages
        },
        {
          id: 6,
          code: 'PR006',
          productName: 'Croissant',
          category: 'Desserts',
          sellingPrice: 80,
          purchasePrice: 50,
          margin: 30,
          branchId: '68ab4a8275017ad6d7d8ad5b',
          branchName: 'Tezlaa Negombo',
          categoryId: '68ab4b9675017ad6d7d8adbd' // Desserts
        },
        {
          id: 7,
          code: 'PR007',
          productName: 'Espresso',
          category: 'Hot Beverages',
          sellingPrice: 90,
          purchasePrice: 60,
          margin: 30,
          branchId: '68ab4a5075017ad6d7d8ad53',
          branchName: 'Tezlaa Colombo',
          categoryId: '68ab4bbe75017ad6d7d8adf2' // Hot Beverages
        },
        {
          id: 8,
          code: 'PR008',
          productName: 'Club Sandwich',
          category: 'Food',
          sellingPrice: 180,
          purchasePrice: 110,
          margin: 70,
          branchId: '68ab4a5075017ad6d7d8ad53',
          branchName: 'Tezlaa Colombo',
          categoryId: '68ab4b8975017ad6d7d8adb8' // Food
        }
      ]
      
      console.log('📊 Total products before filtering:', mockData.length)
      console.log('📊 Sample product categories:', mockData.slice(0, 3).map(p => ({ name: p.productName, categoryId: p.categoryId, category: p.category })))
      
      // Apply branch filter
      if (filters.branch && filters.branch !== 'all' && filters.branch !== '') {
        console.log('🔍 Product Margin Branch Filter - Branch ID:', filters.branch)
        const beforeBranchFilter = mockData.length
        mockData = mockData.filter(item => item.branchId === filters.branch)
        console.log(`📊 Product Margin Branch Filter Results: ${mockData.length}/${beforeBranchFilter} items after filtering`)
      }
      
      // Apply category filter
      if (filters.category && filters.category !== 'all' && filters.category !== '') {
        console.log('🔍 Product Margin Category Filter - Category ID:', filters.category)
        const beforeCategoryFilter = mockData.length
        console.log('🔍 Products before category filter:', mockData.map(p => ({ name: p.productName, categoryId: p.categoryId, matches: p.categoryId === filters.category })))
        mockData = mockData.filter(item => item.categoryId === filters.category)
        console.log(`📊 Product Margin Category Filter Results: ${mockData.length}/${beforeCategoryFilter} items after filtering`)
        console.log('🔍 Filtered products:', mockData.map(p => ({ name: p.productName, category: p.category })))
      }
      
      const totalItems = mockData.length
      const totalMargin = mockData.reduce((sum, item) => sum + item.margin, 0)
      const avgMargin = totalItems > 0 ? totalMargin / totalItems : 0
      
      return {
        success: true,
        data: mockData,
        summary: {
          totalItems,
          totalMargin,
          avgMargin,
          totalRevenue: mockData.reduce((sum, item) => sum + item.sellingPrice, 0),
          totalCost: mockData.reduce((sum, item) => sum + item.purchasePrice, 0)
        }
      }
    } catch (error) {
      console.error('❌ Error generating product margin report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Suppliers Report
   */
  async generateSuppliersReport(filters = {}) {
    try {
      console.log('📊 Generating Suppliers Report with filters:', filters)
      
      // First, fetch ALL suppliers from the system with pagination
      console.log('🏢 Fetching all suppliers from system with pagination')
      let allSuppliers = []
      let currentPage = 1
      const pageLimit = 100 // API maximum limit
      let hasMorePages = true
      
      while (hasMorePages) {
        try {
          console.log(`🏢 Fetching suppliers page ${currentPage} (limit: ${pageLimit})`)
          const suppliersResponse = await SuppliersService.getAllSuppliers({ 
            page: currentPage, 
            limit: pageLimit 
          })
          
          if (suppliersResponse.success) {
            let pageSuppliers = []
            
            // Handle different response structures
            if (suppliersResponse.data?.suppliers && Array.isArray(suppliersResponse.data.suppliers)) {
              pageSuppliers = suppliersResponse.data.suppliers
            } else if (Array.isArray(suppliersResponse.data)) {
              pageSuppliers = suppliersResponse.data
            }
            
            if (pageSuppliers.length > 0) {
              allSuppliers = [...allSuppliers, ...pageSuppliers]
              console.log(`🏢 Added ${pageSuppliers.length} suppliers from page ${currentPage}`)
              
              // Check if we have more pages
              if (pageSuppliers.length < pageLimit) {
                hasMorePages = false // Last page
              } else {
                currentPage++
              }
            } else {
              hasMorePages = false // No more suppliers
            }
          } else {
            console.warn('🏢 API error on page', currentPage, ':', suppliersResponse)
            hasMorePages = false
          }
        } catch (pageError) {
          console.error('🏢 Error fetching suppliers page', currentPage, ':', pageError)
          hasMorePages = false
        }
      }
      
      console.log('🏢 Total suppliers fetched:', allSuppliers.length)
      
      // Then, fetch GRN payments to overlay financial data
      console.log('📦 Fetching GRN payments for financial overlay')
      const grnPaymentsResponse = await GRNService.getAllGRNPayments()
      console.log('📦 GRN payments API response:', grnPaymentsResponse)
      
      // Create payment summary by supplier ID
      const paymentSummary = {}
      if (grnPaymentsResponse.success && grnPaymentsResponse.data?.payments) {
        const payments = grnPaymentsResponse.data.payments
        console.log('📦 Processing', payments.length, 'GRN payments for overlay')
        
        payments.forEach(payment => {
          const grn = payment.grn
          const supplier = grn?.purchaseOrder?.supplier
          
          if (!supplier || !supplier._id) {
            console.warn('⚠️ Skipping payment with missing supplier data:', payment._id)
            return
          }
          
          const supplierId = supplier._id
          
          // Initialize supplier payment summary if not exists
          if (!paymentSummary[supplierId]) {
            paymentSummary[supplierId] = {
              totalPurchase: 0,
              paidAmount: 0,
              grnCount: 0
            }
          }
          
          // Add GRN totals to payment summary
          const grnTotal = grn.totalAmount || 0
          const grnPaid = payment.amountPaid || 0
          
          paymentSummary[supplierId].totalPurchase += grnTotal
          paymentSummary[supplierId].paidAmount += grnPaid
          paymentSummary[supplierId].grnCount += 1
        })
        
        console.log('📦 Generated payment summaries for', Object.keys(paymentSummary).length, 'suppliers')
      }
      
      // Combine all suppliers with their payment data
      let suppliersData = allSuppliers.map(supplier => {
        const supplierId = supplier._id
        const payments = paymentSummary[supplierId] || { totalPurchase: 0, paidAmount: 0, grnCount: 0 }
        
        const totalPurchase = payments.totalPurchase
        const paidAmount = payments.paidAmount  
        const dueAmount = totalPurchase - paidAmount
        
        // Determine payment status
        let status = 'No Transactions'
        if (totalPurchase > 0) {
          if (dueAmount <= 0) {
            status = 'Paid'
          } else if (paidAmount > 0) {
            status = 'Partially Paid'
          } else {
            status = 'Unpaid'
          }
        }
        
        return {
          supplierId: supplierId.slice(-8), // Show last 8 characters like employee ID
          supplierName: supplier.name || 'Unknown Supplier',
          supplierFullId: supplierId, // Keep full ID for filtering
          contactPerson: supplier.contactPerson || '-',
          phone: supplier.phone || '-',
          email: supplier.email || '-',
          address: supplier.address || '-',
          totalPurchase: totalPurchase,
          paidAmount: paidAmount,
          dueAmount: dueAmount,
          status: status,
          grnCount: payments.grnCount,
          isActive: supplier.isActive !== false, // Default to true if not specified
          branchId: 'all', // Suppliers are system-wide
          branchName: 'All Branches'
        }
      })
      
      console.log('🏢 Generated complete supplier report with', suppliersData.length, 'suppliers')

      // Apply filters
      if (filters.supplier) {
        const searchTerm = filters.supplier.toLowerCase()
        suppliersData = suppliersData.filter(item => 
          item.supplierName.toLowerCase().includes(searchTerm) ||
          item.supplierId.toLowerCase().includes(searchTerm) ||
          item.supplierFullId.toLowerCase().includes(searchTerm) ||
          (item.contactPerson && item.contactPerson.toLowerCase().includes(searchTerm))
        )
        console.log('📝 Filtered by supplier search:', searchTerm, '- Results:', suppliersData.length)
      }

      // Note: Branch filtering removed since suppliers are system-wide
      // Each supplier can have transactions with multiple branches
      if (filters.branch && filters.branch !== 'all') {
        console.log('📝 Branch filter ignored - suppliers are system-wide')
      }

      if (filters.paymentType && filters.paymentType !== 'all') {
        const statusMapping = {
          'unpaid': 'Unpaid',
          'paid': 'Paid', 
          'partially_paid': 'Partially Paid',
          'no_transactions': 'No Transactions'
        }
        const targetStatus = statusMapping[filters.paymentType]
        if (targetStatus) {
          suppliersData = suppliersData.filter(item => item.status === targetStatus)
          console.log('📝 Filtered by payment status:', targetStatus, '- Results:', suppliersData.length)
        }
      }

      // Filter by active status if needed
      if (filters.activeOnly) {
        suppliersData = suppliersData.filter(item => item.isActive)
        console.log('📝 Filtered by active status - Results:', suppliersData.length)
      }

      // Calculate summary
      const summary = {
        totalSuppliers: suppliersData.length,
        totalPurchases: suppliersData.reduce((sum, item) => sum + item.totalPurchase, 0),
        totalPaid: suppliersData.reduce((sum, item) => sum + item.paidAmount, 0),
        totalDue: suppliersData.reduce((sum, item) => sum + item.dueAmount, 0),
        paidSuppliers: suppliersData.filter(item => item.status === 'Paid').length,
        unpaidSuppliers: suppliersData.filter(item => item.status === 'Unpaid').length,
        partiallyPaidSuppliers: suppliersData.filter(item => item.status === 'Partially Paid').length,
        noTransactionSuppliers: suppliersData.filter(item => item.status === 'No Transactions').length,
        activeSuppliers: suppliersData.filter(item => item.isActive).length,
        inactiveSuppliers: suppliersData.filter(item => !item.isActive).length
      }

      console.log('✅ Suppliers Report generated successfully:', { 
        dataCount: suppliersData.length, 
        summary 
      })

      return {
        success: true,
        data: suppliersData,
        summary
      }
    } catch (error) {
      console.error('❌ Error generating suppliers report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Salary Reports
   */
  async generateSalaryReports(filters = {}) {
    try {
      console.log('📊 Generating Salary Reports with filters:', filters)
      
      // Try to get real data from salary service
      let salariesData = []
      
      try {
        const response = await salaryService.getSalaries(filters)
        console.log('Salary service response:', response)
        
        if (response && response.success && response.data) {
          if (Array.isArray(response.data.salaries)) {
            salariesData = response.data.salaries
          } else if (Array.isArray(response.data)) {
            salariesData = response.data
          }
        }
      } catch (apiError) {
        console.log('API not available, using mock data:', apiError.message)
      }
      
      // If no real data, return empty array
      if (salariesData.length === 0) {
        salariesData = []
      }
      
      console.log('Raw salaries data:', salariesData.length, 'records')
      
      // Transform data to match table format
      let transformedData = salariesData.map(salary => {
        const employee = salary.employee || {}
        
        console.log('Processing salary record:', {
          salaryId: salary._id,
          employee: employee,
          month: salary.month,
          deductions: salary.deductions,
          allowances: salary.allowances
        })
        
        // Handle different date formats for salary month
        let salaryMonth = salary.salaryMonth || salary.month || ''
        if (salary.month && !salary.salaryMonth) {
          // Convert ISO date format to readable format
          const date = new Date(salary.month)
          if (!isNaN(date.getTime())) {
            salaryMonth = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
          } else {
            salaryMonth = salary.month
          }
        }
        
        // Calculate total allowances from allowances object
        let totalAllowances = 0
        if (salary.allowances && typeof salary.allowances === 'object') {
          totalAllowances = Object.values(salary.allowances).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
        } else {
          totalAllowances = parseFloat(salary.allowances || 0)
        }
        
        // Calculate total deductions from deductions object
        let totalDeductions = 0
        if (salary.deductions && typeof salary.deductions === 'object') {
          totalDeductions = Object.values(salary.deductions).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
        } else {
          totalDeductions = parseFloat(salary.deductions || 0)
        }
        
        // Get employee name - try different field names
        const employeeName = employee.fullName || employee.name || 'N/A'
        
        // For branch, we need to fetch it since it's just an ID in employee.branch
        // For now, use 'N/A' and we'll need to populate it separately
        const branchName = 'N/A' // Will be populated later if needed
        
        return {
          id: salary._id,
          employeeName: employeeName,
          designation: 'N/A', // Will be populated later since job is an ID
          salaryMonth: salaryMonth,
          basicSalary: parseFloat(salary.basicSalary || 0),
          deduction: totalDeductions,
          allowances: totalAllowances,
          netSalary: parseFloat(salary.netSalary || 0),
          branch: branchName,
          paymentStatus: salary.paymentStatus || 'Paid', // Default to Paid since we have actual salary records
          paymentDate: salary.updatedAt || salary.createdAt || null,
          createdAt: salary.createdAt,
          // Keep original data for filtering
          _originalEmployee: employee,
          _originalSalary: salary
        }
      })
      
      console.log('Transformed salary data:', transformedData.length, 'records')
      
      // Populate branch and job names for each record
      try {
        // Get all unique branch IDs
        const branchIds = [...new Set(
          transformedData
            .map(item => item._originalEmployee?.branch)
            .filter(id => id && typeof id === 'string')
        )]
        
        // Get all unique job IDs  
        const jobIds = [...new Set(
          transformedData
            .map(item => item._originalEmployee?.job)
            .filter(id => id && typeof id === 'string')
        )]
        
        console.log('Fetching branch names for IDs:', branchIds)
        console.log('Fetching job names for IDs:', jobIds)
        
        // Fetch branch and job data in parallel
        const [branchesResult, jobsResult] = await Promise.allSettled([
          branchIds.length > 0 ? BranchService.getAllBranches() : Promise.resolve({ success: false }),
          jobIds.length > 0 ? jobService.getAllJobs() : Promise.resolve({ success: false })
        ])
        
        // Create lookup maps
        let branchMap = new Map()
        let jobMap = new Map()
        
        // Handle branches
        if (branchesResult.status === 'fulfilled' && branchesResult.value?.success) {
          console.log('Branches API response:', branchesResult.value)
          const branches = branchesResult.value.data?.branches || branchesResult.value.data || []
          console.log('Extracted branches array:', branches)
          branches.forEach(branch => {
            if (branch._id && branch.name) {
              branchMap.set(branch._id, branch.name)
              console.log(`Branch mapping: ${branch._id} -> ${branch.name}`)
            }
          })
          console.log('Loaded branches map:', branchMap)
        } else {
          console.error('Failed to fetch branches:', branchesResult)
        }
        
        // Handle jobs
        if (jobsResult.status === 'fulfilled' && jobsResult.value?.success) {
          console.log('Jobs API response:', jobsResult.value)
          const jobs = jobsResult.value.data?.jobs || jobsResult.value.data || []
          console.log('Extracted jobs array:', jobs)
          jobs.forEach(job => {
            if (job._id && (job.title || job.name)) {
              const jobTitle = job.title || job.name
              jobMap.set(job._id, jobTitle)
              console.log(`Job mapping: ${job._id} -> ${jobTitle}`)
            }
          })
          console.log('Loaded jobs map:', jobMap)
        } else {
          console.error('Failed to fetch jobs:', jobsResult)
        }
        
        // Update records with actual names
        transformedData = transformedData.map(item => {
          const branchId = item._originalEmployee?.branch
          const jobId = item._originalEmployee?.job
          
          console.log(`Resolving for employee ${item.employeeName}:`)
          console.log(`  Branch ID: ${branchId}, Has mapping: ${branchMap.has(branchId)}, Name: ${branchMap.get(branchId)}`)
          console.log(`  Job ID: ${jobId}, Has mapping: ${jobMap.has(jobId)}, Title: ${jobMap.get(jobId)}`)
          
          return {
            ...item,
            branch: branchId && branchMap.has(branchId) ? branchMap.get(branchId) : `Unknown Branch (ID: ${branchId})`,
            designation: jobId && jobMap.has(jobId) ? jobMap.get(jobId) : `Unknown Position (ID: ${jobId})`
          }
        })
        
        console.log('Updated salary data with branch/job names')
        
      } catch (populateError) {
        console.error('Error populating branch/job names:', populateError)
        // Continue with existing data if population fails
      }
      
      // Apply client-side filtering
      if (filters) {
        const originalCount = transformedData.length
        
        if (filters.branch && filters.branch !== '') {
          transformedData = transformedData.filter(item => 
            item._originalEmployee?.branch === filters.branch
          )
          console.log(`Branch filter (${filters.branch}): ${originalCount} → ${transformedData.length}`)
        }
        
        if (filters.job && filters.job !== '') {
          transformedData = transformedData.filter(item => 
            item.designation.toLowerCase() === filters.job.toLowerCase()
          )
          console.log(`Job filter (${filters.job}): ${transformedData.length} records`)
        }
        
        if (filters.employee && filters.employee !== '') {
          transformedData = transformedData.filter(item => 
            item._originalEmployee?._id === filters.employee
          )
          console.log(`Employee filter (${filters.employee}): ${transformedData.length} records`)
        }
        
        if (filters.startDate) {
          const startDate = new Date(filters.startDate)
          transformedData = transformedData.filter(item => {
            const itemDate = new Date(item.createdAt || item.paymentDate)
            return itemDate >= startDate
          })
          console.log(`Start date filter: ${transformedData.length} records`)
        }
        
        if (filters.endDate) {
          const endDate = new Date(filters.endDate)
          endDate.setHours(23, 59, 59, 999) // End of day
          transformedData = transformedData.filter(item => {
            const itemDate = new Date(item.createdAt || item.paymentDate)
            return itemDate <= endDate
          })
          console.log(`End date filter: ${transformedData.length} records`)
        }
      }
      
      // Calculate summary statistics
      const totalEmployees = transformedData.length
      const totalBasicSalary = transformedData.reduce((sum, item) => sum + item.basicSalary, 0)
      const totalAllowances = transformedData.reduce((sum, item) => sum + item.allowances, 0)
      const totalDeductions = transformedData.reduce((sum, item) => sum + item.deduction, 0)
      const totalNetSalary = transformedData.reduce((sum, item) => sum + item.netSalary, 0)
      const avgNetSalary = totalEmployees > 0 ? totalNetSalary / totalEmployees : 0
      
      const paidSalaries = transformedData.filter(item => item.paymentStatus === 'Paid').length
      const pendingSalaries = transformedData.filter(item => item.paymentStatus === 'Pending').length
      
      // Clean up internal fields before returning
      const cleanedData = transformedData.map(item => {
        const { _originalEmployee, _originalSalary, ...cleanItem } = item
        return cleanItem
      })
      
      return {
        success: true,
        data: cleanedData,
        summary: {
          totalEmployees,
          totalBasicSalary: parseFloat(totalBasicSalary.toFixed(2)),
          totalAllowances: parseFloat(totalAllowances.toFixed(2)),
          totalDeductions: parseFloat(totalDeductions.toFixed(2)),
          totalNetSalary: parseFloat(totalNetSalary.toFixed(2)),
          avgNetSalary: parseFloat(avgNetSalary.toFixed(2)),
          paidSalaries,
          pendingSalaries
        }
      }
    } catch (error) {
      console.error('❌ Error generating salary reports:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Product Sales Report
   */
  async generateProductSalesReport(filters = {}) {
    try {
      console.log('📊 Generating Product Sales Report with filters:', filters)
      
      // Mock data for now
      const mockData = [
        {
          id: 1,
          productName: 'Cappuccino',
          category: 'Hot Beverages',
          unitsSold: 150,
          unitPrice: 120,
          totalRevenue: 18000,
          totalCost: 12000,
          profit: 6000,
          profitMargin: 33.33,
          lastSaleDate: new Date().toISOString()
        }
      ]
      
      // Apply any filters here
      return {
        success: true,
        data: mockData
      }
    } catch (error) {
      console.error('❌ Error generating product sales report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Stock Expiry Report
   */
  async generateStockExpiryReport(filters = {}) {
    try {
      console.log('📊 Generating Stock Expiry Report with filters:', filters)
      
      // Mock data for now
      const mockData = [
        {
          id: 1,
          itemName: 'Milk',
          itemType: 'Raw Material',
          category: 'Dairy',
          batchNumber: 'BT001',
          currentStock: 25,
          expiryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          daysToExpiry: 7,
          stockValue: 1500,
          expiryStatus: 'Expiring Soon'
        }
      ]
      
      // Apply any filters here
      return {
        success: true,
        data: mockData
      }
    } catch (error) {
      console.error('❌ Error generating stock expiry report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Customer Returns Report
   */
  async generateCustomerReturnsReport(filters = {}) {
    try {
      console.log('📊 Generating Customer Returns Report with filters:', filters)
      
      // Prepare API parameters
      const params = {}
      
      if (filters.returnType) params.returnType = filters.returnType
      if (filters.refundMethod) params.refundMethod = filters.refundMethod
      if (filters.search) {
        // Search by order ID or return ID
        params.search = filters.search
      }
      if (filters.branch) params.branchId = filters.branch
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      console.log('🔄 Calling return service with params:', params)
      
      // Fetch customer returns data
      const response = await returnService.getAllOrderReturns(params)
      
      console.log('Customer returns service response:', response)

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch customer returns data')
      }

      // Extract returns data from response
      let returns = []
      if (response.data) {
        // Handle different possible response structures
        if (Array.isArray(response.data)) {
          returns = response.data
        } else if (response.data.orderReturns && Array.isArray(response.data.orderReturns)) {
          returns = response.data.orderReturns
        } else if (response.data.returns && Array.isArray(response.data.returns)) {
          returns = response.data.returns
        } else if (response.data.data && Array.isArray(response.data.data)) {
          returns = response.data.data
        } else {
          console.warn('Unexpected API response structure:', response.data)
          returns = []
        }
      }
      
      console.log(`Raw returns data: ${returns.length} records`)
      console.log('Returns array structure:', returns)

      // Check if we have any returns data to process
      if (!Array.isArray(returns) || returns.length === 0) {
        console.log('No returns data found, returning empty result')
        return {
          success: true,
          data: [],
          summary: {
            totalReturns: 0,
            totalRefundAmount: 0,
            averageRefundAmount: 0,
            returnsByType: {},
            refundsByMethod: {},
            totalItemsReturned: 0
          },
          message: 'No customer returns found matching the filters'
        }
      }

      // Transform returns data for the table
      const transformedReturns = await Promise.all(returns.map(async (returnItem) => {
        console.log('Processing return record:', returnItem)
        
        try {
          // Extract data based on return structure
          const orderId = returnItem.order?.orderNumber || returnItem.orderId || returnItem.order?.orderId || 'N/A'
          const returnDate = returnItem.createdAt || returnItem.returnDate || new Date()
          const returnType = returnItem.returnType || 'customer'
          const refundMethod = returnItem.refundMethod || 'cash'
          const reason = returnItem.reason || returnItem.reasonType || 'N/A'
          const refundAmount = returnItem.totalAmount || returnItem.refundAmount || returnItem.totalRefund || 0
          const itemCount = returnItem.items?.length || returnItem.itemCount || 0
          
          // Get branch name - first check if it's already populated in the order
          let branchName = 'N/A'
          if (returnItem.order?.branch?.name) {
            branchName = returnItem.order.branch.name
          } else if (returnItem.branch && typeof returnItem.branch === 'string') {
            // If branch is just an ID, fetch the name
            try {
              const branchResponse = await BranchService.getBranchById(returnItem.branch)
              if (branchResponse.success && branchResponse.data) {
                branchName = branchResponse.data.name || branchResponse.data.branch?.name || 'Unknown'
              }
            } catch (error) {
              console.warn('Could not fetch branch name for ID:', returnItem.branch, error)
            }
          } else if (returnItem.branch?.name) {
            branchName = returnItem.branch.name
          }

          // Format return type for display
          const formatReturnType = (type) => {
            switch (type?.toLowerCase()) {
              case 'customer': return 'Customer Return'
              case 'internal': return 'Internal Return'
              case 'damage': return 'Damage Return'
              case 'expired': return 'Expired Return'
              default: return type || 'N/A'
            }
          }

          // Format refund method for display
          const formatRefundMethod = (method) => {
            switch (method?.toLowerCase()) {
              case 'cash': return 'Cash Refund'
              case 'exchange': return 'Product Exchange'
              case 'credit': return 'Store Credit'
              case 'card': return 'Card Refund'
              default: return method || 'N/A'
            }
          }

          // Format reason for display
          const formatReason = (reasonValue) => {
            switch (reasonValue?.toLowerCase()) {
              case 'change_mind': return 'Change of Mind'
              case 'expired': return 'Product Expired'
              case 'damage': return 'Product Damaged'
              case 'wrong_prep': return 'Wrong Preparation'
              case 'defective': return 'Defective Product'
              case 'quality_issue': return 'Quality Issue'
              default: return reasonValue || 'N/A'
            }
          }

          return {
            id: returnItem._id || returnItem.id,
            date: returnDate,
            orderId: orderId,
            returnType: formatReturnType(returnType),
            refundMethod: formatRefundMethod(refundMethod),
            reason: formatReason(reason),
            refundAmount: parseFloat(refundAmount) || 0,
            itemCount: parseInt(itemCount) || 0,
            branch: branchName,
            // Keep original data for reference
            _original: returnItem
          }
        } catch (error) {
          console.error('Error processing return record:', error, returnItem)
          // Return minimal data in case of processing error
          return {
            id: returnItem._id || returnItem.id || 'unknown',
            date: returnItem.createdAt || new Date(),
            orderId: returnItem.orderId || 'N/A',
            returnType: returnItem.returnType || 'N/A',
            refundMethod: returnItem.refundMethod || 'N/A',
            reason: returnItem.reason || 'N/A',
            refundAmount: 0,
            itemCount: 0,
            branch: 'N/A',
            _original: returnItem
          }
        }
      }))

      console.log(`Transformed returns data: ${transformedReturns.length} records`)

      // Client-side filtering if needed
      let filteredReturns = transformedReturns

      // Apply search filter - always do client-side filtering to ensure it works
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase().trim()
        console.log('🔍 Returns Search Filter - Term:', searchTerm)
        
        filteredReturns = filteredReturns.filter(item => {
          const orderId = (item.orderId || '').toLowerCase()
          const returnId = (item.id || '').toLowerCase()
          const branch = (item.branch || '').toLowerCase()
          
          const matches = orderId.includes(searchTerm) ||
                         returnId.includes(searchTerm) ||
                         branch.includes(searchTerm)
          
          if (matches) {
            console.log('✅ Returns Search Match:', { 
              orderId: item.orderId,
              returnId: item.id,
              branch: item.branch,
              searchTerm 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Returns Search Results: ${filteredReturns.length} returns after filtering`)
      }

      // Calculate summary statistics
      const summary = {
        totalReturns: filteredReturns.length,
        totalRefundAmount: filteredReturns.reduce((sum, item) => sum + (item.refundAmount || 0), 0),
        averageRefundAmount: filteredReturns.length > 0 
          ? filteredReturns.reduce((sum, item) => sum + (item.refundAmount || 0), 0) / filteredReturns.length 
          : 0,
        returnsByType: filteredReturns.reduce((acc, item) => {
          const type = item.returnType || 'Unknown'
          acc[type] = (acc[type] || 0) + 1
          return acc
        }, {}),
        refundsByMethod: filteredReturns.reduce((acc, item) => {
          const method = item.refundMethod || 'Unknown'
          acc[method] = (acc[method] || 0) + 1
          return acc
        }, {}),
        totalItemsReturned: filteredReturns.reduce((sum, item) => sum + (item.itemCount || 0), 0)
      }

      console.log('Generated customer returns summary:', summary)

      return {
        success: true,
        data: filteredReturns,
        summary: summary,
        message: `Customer returns report generated successfully with ${filteredReturns.length} records`
      }

    } catch (error) {
      console.error('❌ Error generating customer returns report:', error)
      return {
        success: false,
        data: [],
        summary: {},
        error: error.message
      }
    }
  }

  /**
   * Generate Supplier Returns Report
   */
  async generateSupplierReturnsReport(filters = {}) {
    try {
      console.log('📊 Generating Supplier Returns Report with filters:', filters)
      
      // Get supplier return notes from the API with filters
      const response = await SupplierReturnService.getAllSupplierReturnNotes(filters)
      console.log('📦 Supplier return notes API response:', response)
      
      // Extract data from response - handle the actual API response structure: data.data.srns
      let supplierReturns = []
      if (response.success || response.data?.srns || response.srns) {
        // Handle the actual API response format: response.data.srns
        if (response.data && Array.isArray(response.data.srns)) {
          supplierReturns = response.data.srns
        }
        // Handle alternative API format: response.srns
        else if (Array.isArray(response.srns)) {
          supplierReturns = response.srns
        } 
        // Handle other alternative formats
        else if (response.data && response.data.supplierReturnNotes && Array.isArray(response.data.supplierReturnNotes)) {
          supplierReturns = response.data.supplierReturnNotes
        } else if (Array.isArray(response.data)) {
          supplierReturns = response.data
        }
      }
      
      console.log(`📦 Extracted ${supplierReturns.length} supplier return records`)
      
      // Debug: Log the actual data structure to understand the API format
      if (supplierReturns.length > 0) {
        console.log('🔍 First supplier return item structure:', supplierReturns[0])
        if (supplierReturns[0].returnedItems && supplierReturns[0].returnedItems.length > 0) {
          console.log('🔍 First returned item structure:', supplierReturns[0].returnedItems[0])
        }
      }
      
      // If no data from API, use mock data for testing
      if (supplierReturns.length === 0) {
        console.warn('⚠️ No supplier return data found, using mock data')
        supplierReturns = [
          {
            _id: '67123abc456def789',
            returnId: 'SRN001',
            purchaseOrder: {
              _id: 'po123',
              poNumber: 'PO-2025-001',
              supplier: {
                name: 'ABC Coffee Suppliers'
              }
            },
            returnedItems: [
              {
                rawMaterial: {
                  name: 'Coffee Beans'
                },
                quantityReturned: 25,
                unitPrice: 250,
                totalAmount: 6250
              }
            ],
            returnReason: 'Quality issue - beans were stale',
            returnDate: new Date('2025-09-28').toISOString(),
            status: 'Processed',
            createdBy: {
              firstName: 'Purchase',
              lastName: 'Manager'
            }
          },
          {
            _id: '67123abc456def790',
            returnId: 'SRN002',
            purchaseOrder: {
              _id: 'po124',
              poNumber: 'PO-2025-002',
              supplier: {
                name: 'Fresh Dairy Ltd'
              }
            },
            returnedItems: [
              {
                rawMaterial: {
                  name: 'Milk'
                },
                quantityReturned: 50,
                unitPrice: 120,
                totalAmount: 6000
              }
            ],
            returnReason: 'Expired products received',
            returnDate: new Date('2025-09-30').toISOString(),
            status: 'Pending',
            createdBy: {
              firstName: 'Store',
              lastName: 'Manager'
            }
          }
        ]
      }
      
      // Transform data to flat structure for the report
      const transformedData = []
      
      supplierReturns.forEach(returnNote => {
        // Each returned item becomes a separate row in the report
        if (returnNote.returnedItems && Array.isArray(returnNote.returnedItems) && returnNote.returnedItems.length > 0) {
          returnNote.returnedItems.forEach(item => {
            // Extract data based on actual API response structure
            const returnId = returnNote.srnNumber || returnNote._id || 'N/A'
            const poNumber = returnNote.purchaseOrder?.poNumber || 'N/A'
            const supplierName = returnNote.supplier?.name || 'Unknown Supplier'
            const branchName = returnNote.branch?.name || 'Unknown Branch'
            
            // Get item name from snapshot data (rawMaterialSnapshot or productSnapshot)
            const itemName = item.rawMaterialSnapshot?.name || item.productSnapshot?.name || 'Unknown Item'
            
            const quantity = item.returnedQuantity || 0
            const unitPrice = item.unitPrice || 0
            const totalAmount = item.totalPrice || 0
            const reason = returnNote.note || 'No reason provided'
            const returnDate = returnNote.returnedDate || returnNote.createdAt || new Date().toISOString()
            const processedBy = returnNote.returnBy || returnNote.createdBy?.username || 'Unknown'
            const status = 'Completed' // All returned items are completed

            transformedData.push({
              id: `${returnNote._id || returnNote.id}-${item._id || Math.random()}`,
              returnId,
              purchaseOrderNumber: poNumber,
              supplierName,
              branchName,
              itemName,
              quantity,
              returnAmount: totalAmount,
              reason,
              returnDate,
              processedBy,
              status
            })
          })
        } else {
          // If no returnedItems array, create a single row for the return note
          const returnId = returnNote.srnNumber || returnNote._id || 'N/A'
          const poNumber = returnNote.purchaseOrder?.poNumber || 'N/A'
          const supplierName = returnNote.supplier?.name || 'Unknown Supplier'
          const branchName = returnNote.branch?.name || 'Unknown Branch'
          const reason = returnNote.note || 'No reason provided'
          const returnDate = returnNote.returnedDate || returnNote.createdAt || new Date().toISOString()
          const processedBy = returnNote.returnBy || returnNote.createdBy?.username || 'Unknown'
          const status = 'Completed'

          transformedData.push({
            id: returnNote._id || returnNote.id || Math.random(),
            returnId,
            purchaseOrderNumber: poNumber,
            supplierName,
            branchName,
            itemName: 'Multiple Items',
            quantity: 0,
            returnAmount: returnNote.totalAmount || 0,
            reason,
            returnDate,
            processedBy,
            status
          })
        }
      })
      
      console.log(`📊 Transformed ${transformedData.length} supplier return report rows`)
      
      // Apply client-side filtering as fallback if API doesn't support filtering
      let filteredData = transformedData
      
      // Date filtering
      if (filters.fromDate && filters.fromDate !== '') {
        console.log('🔍 Supplier Returns From Date Filter - Date:', filters.fromDate)
        filteredData = filteredData.filter(item => {
          const returnDate = new Date(item.returnDate)
          const fromDate = new Date(filters.fromDate)
          fromDate.setHours(0, 0, 0, 0)
          const matches = returnDate >= fromDate
          
          if (matches) {
            console.log('✅ Supplier Returns From Date Match:', { 
              returnDate: returnDate.toDateString(),
              fromDate: fromDate.toDateString(),
              returnId: item.returnId
            })
          }
          
          return matches
        })
        console.log(`📊 Supplier Returns From Date Results: ${filteredData.length} items after filtering`)
      }
      
      if (filters.toDate && filters.toDate !== '') {
        console.log('🔍 Supplier Returns To Date Filter - Date:', filters.toDate)
        filteredData = filteredData.filter(item => {
          const returnDate = new Date(item.returnDate)
          const toDate = new Date(filters.toDate)
          toDate.setHours(23, 59, 59, 999)
          const matches = returnDate <= toDate
          
          if (matches) {
            console.log('✅ Supplier Returns To Date Match:', { 
              returnDate: returnDate.toDateString(),
              toDate: toDate.toDateString(),
              returnId: item.returnId
            })
          }
          
          return matches
        })
        console.log(`📊 Supplier Returns To Date Results: ${filteredData.length} items after filtering`)
      }
      
      // Supplier filtering
      if (filters.supplier && filters.supplier !== '') {
        console.log('🔍 Supplier Returns Supplier Filter - Supplier:', filters.supplier)
        filteredData = filteredData.filter(item => {
          const supplierMatch = item.supplierName && item.supplierName.toLowerCase().includes(filters.supplier.toLowerCase())
          
          if (supplierMatch) {
            console.log('✅ Supplier Returns Supplier Match:', { 
              supplierName: item.supplierName,
              filter: filters.supplier,
              returnId: item.returnId
            })
          }
          
          return supplierMatch
        })
        console.log(`📊 Supplier Returns Supplier Results: ${filteredData.length} items after filtering`)
      }
      
      // Branch filtering
      if (filters.branch && filters.branch !== '') {
        console.log('🔍 Supplier Returns Branch Filter - Branch:', filters.branch)
        filteredData = filteredData.filter(item => {
          const branchMatch = item.branchName && item.branchName.toLowerCase().includes(filters.branch.toLowerCase())
          
          if (branchMatch) {
            console.log('✅ Supplier Returns Branch Match:', { 
              branchName: item.branchName,
              filter: filters.branch,
              returnId: item.returnId
            })
          }
          
          return branchMatch
        })
        console.log(`📊 Supplier Returns Branch Results: ${filteredData.length} items after filtering`)
      }
      
      // Search filtering (returnId, poNumber, itemName)
      if (filters.search && filters.search !== '') {
        console.log('🔍 Supplier Returns Search Filter - Term:', filters.search)
        const searchTerm = filters.search.toLowerCase()
        filteredData = filteredData.filter(item => {
          const returnIdMatch = item.returnId && item.returnId.toLowerCase().includes(searchTerm)
          const poNumberMatch = item.purchaseOrderNumber && item.purchaseOrderNumber.toLowerCase().includes(searchTerm)
          const itemNameMatch = item.itemName && item.itemName.toLowerCase().includes(searchTerm)
          const supplierMatch = item.supplierName && item.supplierName.toLowerCase().includes(searchTerm)
          
          const matches = returnIdMatch || poNumberMatch || itemNameMatch || supplierMatch
          
          if (matches) {
            console.log('✅ Supplier Returns Search Match:', { 
              returnId: item.returnId,
              poNumber: item.purchaseOrderNumber,
              itemName: item.itemName,
              supplier: item.supplierName,
              searchTerm
            })
          }
          
          return matches
        })
        console.log(`📊 Supplier Returns Search Results: ${filteredData.length} items after filtering`)
      }
      
      console.log(`📊 Final filtered supplier returns data: ${filteredData.length} items`)
      
      return {
        success: true,
        data: filteredData
      }
    } catch (error) {
      console.error('❌ Error generating supplier returns report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }

  /**
   * Generate Profits & Loss Report
   */
  async generateProfitsLossReport(filters = {}) {
    try {
      console.log('📊 Generating Profits & Loss Report with filters:', filters)
      
      // Mock data for now
      const mockData = [
        {
          id: 1,
          period: 'January 2024',
          totalRevenue: 500000,
          totalCosts: 350000,
          grossProfit: 150000,
          operatingExpenses: 80000,
          netProfit: 70000,
          profitMargin: 14.0,
          branch: 'Main Branch',
          status: 'Profitable'
        }
      ]
      
      // Apply any filters here
      return {
        success: true,
        data: mockData
      }
    } catch (error) {
      console.error('❌ Error generating profits & loss report:', error)
      return { success: false, error: error.message, data: [] }
    }
  }



  /**
   * Export data to CSV format
   */
  exportToCSV(data, filename = 'report') {
    try {
      if (!data || data.length === 0) {
        console.warn('⚠️ No data to export')
        return
      }
      
      const headers = Object.keys(data[0]).join(',')
      const csvContent = [
        headers,
        ...data.map(row => 
          Object.values(row).map(value => 
            typeof value === 'string' && value.includes(',') 
              ? `"${value}"` 
              : value
          ).join(',')
        )
      ].join('\n')
      
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${filename}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
      
      console.log('✅ CSV export completed')
    } catch (error) {
      console.error('❌ Error exporting to CSV:', error)
    }
  }

  async generateLeaveReports(filters = {}) {
    try {
      console.log('📊 Generating Leave Reports with filters:', filters)
      
      // Import the leave service
      const { leaveService } = await import('./leaveService')
      const EmployeeService = (await import('./employeeService')).default
      const BranchService = (await import('./branchService')).default
      
      // Prepare API parameters
      const params = {}
      
      if (filters.leaveType) params.leaveType = filters.leaveType
      if (filters.employee) params.employeeId = filters.employee
      if (filters.search) params.search = filters.search
      if (filters.branch) params.branchId = filters.branch
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      console.log('🔄 Calling leave service with params:', params)
      
      // Fetch leave data
      const response = await leaveService.getAllLeaves(params)
      
      console.log('Leave service response:', response)

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch leave data')
      }

      // Extract leaves data from response
      let leaves = []
      if (response.data) {
        // Handle different possible response structures
        if (Array.isArray(response.data)) {
          leaves = response.data
        } else if (response.data.leaves && Array.isArray(response.data.leaves)) {
          leaves = response.data.leaves
        } else if (response.data.data && Array.isArray(response.data.data)) {
          leaves = response.data.data
        } else {
          console.warn('Unexpected API response structure:', response.data)
          leaves = []
        }
      }
      
      console.log(`Raw leaves data: ${leaves.length} records`)
      console.log('Leaves array structure:', leaves)

      // Check if we have any leaves data to process
      if (!Array.isArray(leaves) || leaves.length === 0) {
        console.log('No leaves data found, returning empty result')
        return {
          success: true,
          data: [],
          summary: {
            totalLeaves: 0,
            approvedLeaves: 0,
            pendingLeaves: 0,
            rejectedLeaves: 0,
            totalDays: 0,
            leavesByType: {},
            leavesByStatus: {},
            leavesByBranch: {}
          },
          message: 'No leave records found matching the filters'
        }
      }

      // Transform leaves data for the table
      const transformedLeaves = await Promise.all(leaves.map(async (leave) => {
        console.log('Processing leave record:', leave)
        
        try {
          // Extract basic leave information
          const leaveId = leave._id || leave.id
          const startDate = leave.startDate || leave.date || leave.createdAt
          const endDate = leave.endDate || leave.startDate || leave.date
          const leaveType = leave.leaveType?.name || leave.leaveType?.typeName || leave.leaveTypeName || 'N/A'
          const status = leave.status || 'pending'
          const reason = leave.reason || 'N/A'
          
          // Calculate number of leave days
          let noOfLeaves = 1
          if (startDate && endDate) {
            try {
              const start = new Date(startDate)
              const end = new Date(endDate)
              const diffTime = Math.abs(end - start)
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
              noOfLeaves = Math.max(1, diffDays)
            } catch (error) {
              console.warn('Could not calculate leave days:', error)
              noOfLeaves = leave.days || leave.noOfDays || 1
            }
          } else if (leave.days || leave.noOfDays) {
            noOfLeaves = leave.days || leave.noOfDays
          }
          
          // Get employee information
          let employeeId = 'N/A'
          let employeeName = 'N/A'
          
          console.log('🔍 Employee object structure:', leave.employee)
          
          if (leave.employee) {
            // If employee is populated as an object
            if (typeof leave.employee === 'object') {
              console.log('🔍 Employee object keys:', Object.keys(leave.employee))
              console.log('🔍 Employee name field:', leave.employee.name)
              console.log('🔍 Employee firstName:', leave.employee.firstName)
              console.log('🔍 Employee lastName:', leave.employee.lastName)
              console.log('🔍 Employee username:', leave.employee.username)
              console.log('🔍 Employee fullName:', leave.employee.fullName)
              console.log('🔍 Employee displayName:', leave.employee.displayName)
              console.log('🔍 Employee employeeId field:', leave.employee.employeeId)
              console.log('🔍 Employee id field:', leave.employee.id)
              console.log('🔍 Employee _id field:', leave.employee._id)
              
              // Extract employee ID - prioritize the human-readable employeeId over MongoDB _id
              let rawEmployeeId = leave.employee.employeeId || leave.employee.id || leave.employee._id || 'N/A'
              console.log('🔍 Raw employee ID value:', rawEmployeeId)
              console.log('🔍 Raw employee ID type:', typeof rawEmployeeId)
              console.log('🔍 Raw employee ID length:', rawEmployeeId?.length)
              
              // Check if it's already in the correct format (e + 8 characters)
              if (rawEmployeeId && rawEmployeeId.match(/^e[a-fA-F0-9]{8}$/)) {
                employeeId = rawEmployeeId
                console.log('✅ Employee ID already in correct format:', employeeId)
              }
              // If we have a MongoDB ObjectId (24 hex chars), convert it
              else if (rawEmployeeId && rawEmployeeId.length === 24 && rawEmployeeId.match(/^[a-fA-F0-9]{24}$/)) {
                const last8 = rawEmployeeId.slice(-8)
                // Check if the last 8 characters already start with 'e'
                if (last8.startsWith('e')) {
                  employeeId = last8  // Use as-is since it already has 'e' prefix
                } else {
                  employeeId = 'e' + last8  // Add 'e' prefix
                }
                console.log('🔄 Converted MongoDB ID to short format:', rawEmployeeId, '->', employeeId)
              }
              // Otherwise use as-is but remove any duplicate 'e' prefix
              else {
                // Check if it starts with 'ee' and fix it
                if (rawEmployeeId && rawEmployeeId.startsWith('ee') && rawEmployeeId.length === 10) {
                  employeeId = rawEmployeeId.substring(1) // Remove the first 'e'
                  console.log('🛠️ Fixed duplicate e prefix:', rawEmployeeId, '->', employeeId)
                } else {
                  employeeId = rawEmployeeId
                  console.log('📝 Using employee ID as-is:', employeeId)
                }
              }
              
              console.log('🎯 Final employee ID:', employeeId)
              
              // Try multiple name extraction patterns
              let nameResult = 'N/A'
              
              if (leave.employee.name) {
                nameResult = leave.employee.name
              } else if (leave.employee.fullName) {
                nameResult = leave.employee.fullName
              } else if (leave.employee.displayName) {
                nameResult = leave.employee.displayName
              } else if (leave.employee.firstName || leave.employee.lastName) {
                const firstName = leave.employee.firstName || ''
                const lastName = leave.employee.lastName || ''
                nameResult = `${firstName} ${lastName}`.trim()
              } else if (leave.employee.username) {
                nameResult = leave.employee.username
              }
              
              employeeName = nameResult || 'N/A'
              console.log('🎯 Final employee name:', employeeName)
            } 
            // If employee is just an ID, try to fetch employee data
            else if (typeof leave.employee === 'string') {
              try {
                const employeeResponse = await EmployeeService.getEmployeeById(leave.employee)
                if (employeeResponse.success && employeeResponse.data) {
                  const emp = employeeResponse.data
                  // Extract employee ID - prioritize the human-readable employeeId over MongoDB _id
                  let rawEmpId = emp.employeeId || emp.id || emp._id || 'Unknown'
                  
                  // Check if it's already in the correct format (e + 8 characters)
                  if (rawEmpId && rawEmpId.match(/^e[a-fA-F0-9]{8}$/)) {
                    employeeId = rawEmpId
                  }
                  // If we have a MongoDB ObjectId (24 hex chars), convert it
                  else if (rawEmpId && rawEmpId.length === 24 && rawEmpId.match(/^[a-fA-F0-9]{24}$/)) {
                    const last8 = rawEmpId.slice(-8)
                    // Check if the last 8 characters already start with 'e'
                    if (last8.startsWith('e')) {
                      employeeId = last8  // Use as-is since it already has 'e' prefix
                    } else {
                      employeeId = 'e' + last8  // Add 'e' prefix
                    }
                    console.log('🔄 Converted fetched MongoDB ID to short format:', rawEmpId, '->', employeeId)
                  }
                  // Otherwise use as-is
                  else {
                    employeeId = rawEmpId
                  }
                  
                  employeeName = emp.name || 
                               `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 
                               emp.username || 'Unknown'
                  console.log('🎯 Fetched employee ID:', employeeId, 'Name:', employeeName)
                }
              } catch (error) {
                console.warn('Could not fetch employee data for ID:', leave.employee, error)
                employeeId = leave.employee
                employeeName = 'Unknown Employee'
              }
            }
          } else if (leave.employeeId) {
            let rawLeaveEmpId = leave.employeeId
            
            // Check if it's already in the correct format (e + 8 characters)
            if (rawLeaveEmpId && rawLeaveEmpId.match(/^e[a-fA-F0-9]{8}$/)) {
              employeeId = rawLeaveEmpId
            }
            // If we have a MongoDB ObjectId (24 hex chars), convert it
            else if (rawLeaveEmpId && rawLeaveEmpId.length === 24 && rawLeaveEmpId.match(/^[a-fA-F0-9]{24}$/)) {
              const last8 = rawLeaveEmpId.slice(-8)
              employeeId = last8.startsWith('e') ? last8 : 'e' + last8
              console.log('🔄 Converted direct leave MongoDB ID to short format:', rawLeaveEmpId, '->', employeeId)
            }
            // Otherwise use as-is
            else {
              employeeId = rawLeaveEmpId
            }
            
            employeeName = leave.employeeName || 'N/A'
            console.log('🎯 Direct leave employeeId:', employeeId, 'Name:', employeeName)
          }
          
          // Get branch information
          let branchName = 'N/A'
          
          if (leave.branch) {
            // If branch is populated as an object
            if (typeof leave.branch === 'object') {
              branchName = leave.branch.name || 'Unknown Branch'
            }
            // If branch is just an ID, try to fetch branch data
            else if (typeof leave.branch === 'string') {
              try {
                const branchResponse = await BranchService.getBranchById(leave.branch)
                if (branchResponse.success && branchResponse.data) {
                  const branchData = branchResponse.data.branch || branchResponse.data
                  branchName = branchData.name || 'Unknown Branch'
                }
              } catch (error) {
                console.warn('Could not fetch branch data for ID:', leave.branch, error)
                branchName = 'Unknown Branch'
              }
            }
          } else if (leave.branchName) {
            branchName = leave.branchName
          }

          // Format status for display
          const formatStatus = (status) => {
            switch (status?.toLowerCase()) {
              case 'approved': return 'Approved'
              case 'pending': return 'Pending'
              case 'rejected': return 'Rejected'
              case 'cancelled': return 'Cancelled'
              default: return status || 'Pending'
            }
          }

          return {
            id: leaveId,
            employeeId: employeeId,
            employeeName: employeeName,
            leaveType: leaveType,
            date: startDate,
            noOfLeaves: parseInt(noOfLeaves) || 1,
            status: formatStatus(status),
            branch: branchName,
            // Additional fields for filtering/debugging
            startDate: startDate,
            endDate: endDate,
            reason: reason,
            _original: leave
          }
        } catch (error) {
          console.error('Error processing leave record:', error, leave)
          // Return minimal data in case of processing error
          return {
            id: leave._id || leave.id || 'unknown',
            employeeId: leave.employeeId || 'N/A',
            employeeName: leave.employeeName || 'N/A',
            leaveType: leave.leaveType?.name || 'N/A',
            date: leave.startDate || leave.date || new Date(),
            noOfLeaves: 1,
            status: leave.status || 'Pending',
            branch: 'N/A',
            _original: leave
          }
        }
      }))

      console.log(`Transformed leaves data: ${transformedLeaves.length} records`)

      // Client-side filtering if needed
      let filteredLeaves = transformedLeaves

      // Apply additional client-side filtering to ensure filters work
      
      // Apply branch filter
      if (filters.branch) {
        console.log('🏢 Leave Branch Filter - Branch ID:', filters.branch)
        
        filteredLeaves = filteredLeaves.filter(item => {
          // Check if the leave record has branch information
          const leaveBranchId = item._original?.branch?._id || item._original?.branchId || item._original?.branch
          const matches = leaveBranchId === filters.branch
          
          if (matches) {
            console.log('✅ Leave Branch Match:', { 
              leaveBranchId,
              filterBranch: filters.branch,
              employeeName: item.employeeName 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Leave Branch Results: ${filteredLeaves.length} leaves after branch filtering`)
      }

      // Apply search filter - always do client-side filtering to ensure it works
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase().trim()
        console.log('🔍 Leave Search Filter - Term:', searchTerm)
        
        filteredLeaves = filteredLeaves.filter(item => {
          const employeeId = (item.employeeId || '').toLowerCase()
          const employeeName = (item.employeeName || '').toLowerCase()
          const leaveId = (item.id || '').toLowerCase()
          
          const matches = employeeId.includes(searchTerm) ||
                         employeeName.includes(searchTerm) ||
                         leaveId.includes(searchTerm)
          
          if (matches) {
            console.log('✅ Leave Search Match:', { 
              employeeId: item.employeeId,
              employeeName: item.employeeName,
              leaveId: item.id,
              searchTerm 
            })
          }
          
          return matches
        })
        
        console.log(`📊 Leave Search Results: ${filteredLeaves.length} leaves after filtering`)
      }

      // Calculate summary statistics
      const summary = {
        totalLeaves: filteredLeaves.length,
        approvedLeaves: filteredLeaves.filter(item => item.status.toLowerCase() === 'approved').length,
        pendingLeaves: filteredLeaves.filter(item => item.status.toLowerCase() === 'pending').length,
        rejectedLeaves: filteredLeaves.filter(item => item.status.toLowerCase() === 'rejected').length,
        totalDays: filteredLeaves.reduce((sum, item) => sum + (item.noOfLeaves || 0), 0),
        leavesByType: filteredLeaves.reduce((acc, item) => {
          const type = item.leaveType || 'Unknown'
          acc[type] = (acc[type] || 0) + 1
          return acc
        }, {}),
        leavesByStatus: filteredLeaves.reduce((acc, item) => {
          const status = item.status || 'Unknown'
          acc[status] = (acc[status] || 0) + 1
          return acc
        }, {}),
        leavesByBranch: filteredLeaves.reduce((acc, item) => {
          const branch = item.branch || 'Unknown'
          acc[branch] = (acc[branch] || 0) + 1
          return acc
        }, {})
      }

      console.log('📊 Leave Reports Summary:', summary)

      return {
        success: true,
        data: filteredLeaves,
        summary: summary,
        message: `Successfully generated leave report with ${filteredLeaves.length} records`
      }

    } catch (error) {
      console.error('Error generating leave reports:', error)
      return {
        success: false,
        data: [],
        summary: {
          totalLeaves: 0,
          approvedLeaves: 0,
          pendingLeaves: 0,
          rejectedLeaves: 0,
          totalDays: 0,
          leavesByType: {},
          leavesByStatus: {},
          leavesByBranch: {}
        },
        message: error.message || 'Failed to generate leave reports',
        error: error
      }
    }
  }

  /**
   * Export data to JSON format
   */
  exportToJSON(data, filename = 'report') {
    try {
      if (!data) {
        console.warn('⚠️ No data to export')
        return
      }
      
      const jsonContent = JSON.stringify(data, null, 2)
      const blob = new Blob([jsonContent], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${filename}.json`
      link.click()
      window.URL.revokeObjectURL(url)
      
      console.log('✅ JSON export completed')
    } catch (error) {
      console.error('❌ Error exporting to JSON:', error)
    }
  }

  /**
   * Export multiple reports to PDF
   */
  async exportToPDF(reportData, selectedReports, globalFilters) {
    try {
      console.log('📄 Starting PDF export for multiple reports', {
        reportData: Object.keys(reportData),
        selectedReports,
        reportDataSizes: Object.fromEntries(Object.entries(reportData).map(([key, value]) => [key, value?.length || 0]))
      })
      
      // Use direct imports that should be available
      const jsPDF = window.jspdf?.jsPDF || (await import('jspdf')).jsPDF
      
      // Import autotable plugin
      if (!window.jspdf?.jsPDF?.API?.autoTable) {
        await import('jspdf-autotable')
      }
      
      // Create PDF document in landscape mode for better column layout
      const doc = new jsPDF('landscape', 'pt', 'a4')
      console.log('📄 PDF document created, autoTable available:', typeof doc.autoTable)
      
      // Set document properties
      doc.setProperties({
        title: 'Tezlaa Cafe Reports',
        creator: 'Tezlaa Cafe Management System',
        author: 'Tezlaa System'
      })
      
      let isFirstReport = true
      const pageWidth = doc.internal.pageSize.getWidth()
      
      for (const reportType of selectedReports) {
        const data = reportData[reportType] || []
        
        console.log(`🔍 Processing report: ${reportType}`)
        console.log(`🔍 Data length: ${data.length}`)
        console.log(`🔍 First data item:`, data[0])
        
        if (!isFirstReport) {
          doc.addPage()
        }
        isFirstReport = false
        
        // Add report title
        doc.setFontSize(18)
        doc.setFont('helvetica', 'bold')
        const reportTitle = this.getReportTitle(reportType)
        const titleWidth = doc.getTextWidth(reportTitle)
        doc.text(reportTitle, (pageWidth - titleWidth) / 2, 40)
        
        // Add generation info
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        const dateText = `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`
        const dateWidth = doc.getTextWidth(dateText)
        doc.text(dateText, (pageWidth - dateWidth) / 2, 60)
        
        const recordText = `Records: ${data.length} ${Object.keys(globalFilters).some(key => globalFilters[key]) ? '(Filtered)' : '(All)'}`
        const recordWidth = doc.getTextWidth(recordText)
        doc.text(recordText, (pageWidth - recordWidth) / 2, 75)
        
        // Add line under header
        doc.setLineWidth(1)
        doc.line(40, 85, pageWidth - 40, 85)
        
        if (data.length === 0) {
          doc.setFontSize(12)
          doc.text('No data available for this report', 40, 120)
          continue
        }
        
        // Get column configuration
        const columns = this.getReportColumns(reportType)
        console.log(`🔧 Columns for ${reportType}:`, columns)
        console.log(`🔧 Columns type:`, typeof columns)
        console.log(`🔧 Columns is array:`, Array.isArray(columns))
        console.log(`🔧 Columns length:`, columns.length)
        
        // Prepare headers - ensure all columns have proper labels
        const headers = columns.map(col => {
          const header = col.label || col.header || col.title || col.key || 'Column'
          return String(header).trim() || 'Column'
        })
        
        console.log(`📋 Headers for ${reportType}:`, headers)
        console.log(`📋 Headers array length: ${headers.length}`)
        
        // Additional validation
        if (headers.length === 0) {
          console.error(`❌ No headers found for report type: ${reportType}`)
          console.error(`❌ Available column mappings: ${Object.keys(this.columnMapping || {})}`)
          continue
        }
        
        // Prepare table data
        const tableData = data.map(row => 
          columns.map(col => {
            let value = row[col.key] || row[col.dataIndex] || ''
            
            // Apply render function if exists
            if (col.render && typeof col.render === 'function') {
              try {
                value = col.render(value, row)
                // If render returns JSX, extract text content
                if (typeof value === 'object' && value !== null) {
                  value = value.props?.children || String(value)
                }
              } catch (error) {
                console.warn('Error in column render function:', error)
              }
            }
            
            return String(value || '').replace(/[^\w\s.,()-]/g, '') // Clean special characters
          })
        )
        
        console.log(`📊 Report ${reportType}:`, {
          headers: headers,
          headerCount: headers.length,
          dataRows: tableData.length,
          firstRowCols: tableData[0]?.length || 0,
          columnsConfig: columns,
          sampleData: data[0]
        })
        
        // Always use manual table creation for now to ensure headers work correctly
        console.log('📊 Creating table for', reportType, 'with headers:', headers)
        this.createManualTable(doc, headers, tableData, 100)
      }
      
      // Save the PDF
      const fileName = `Tezlaa_Reports_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      
      console.log('✅ PDF export completed:', fileName)
      
    } catch (error) {
      console.error('❌ Error exporting to PDF:', error)
      throw error
    }
  }

  /**
   * Export multiple reports to Excel
   */
  async exportToExcel(reportData, selectedReports, globalFilters) {
    try {
      console.log('📊 Starting Excel export for multiple reports', { globalFilters })
      
      // Import xlsx dynamically
      const XLSX = await import('xlsx')
      
      const workbook = XLSX.utils.book_new()
      
      for (const reportType of selectedReports) {
        const data = reportData[reportType] || []
        const columns = this.getReportColumns(reportType)
        
        if (data.length === 0) continue
        
        // Prepare worksheet data
        const worksheetData = [
          // Headers
          columns.map(col => col.label || col.key),
          // Data rows
          ...data.map(row => 
            columns.map(col => {
              let value = row[col.key] || ''
              
              // Apply render function if exists
              if (col.render && typeof col.render === 'function') {
                try {
                  value = col.render(value, row)
                  if (typeof value === 'object' && value !== null) {
                    value = value.props?.children || String(value)
                  }
                } catch (error) {
                  console.warn('Error in column render function:', error)
                }
              }
              
              return String(value || '').replace(/[^\w\s.,()-]/g, '')
            })
          )
        ]
        
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
        
        // Set column widths
        const colWidths = columns.map(() => ({ wch: 15 }))
        worksheet['!cols'] = colWidths
        
        const sheetName = this.getReportTitle(reportType).replace(/[^\w\s]/g, '').substring(0, 31)
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      }
      
      const fileName = `Tezlaa_Reports_${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(workbook, fileName)
      
      console.log('✅ Excel export completed:', fileName)
      
    } catch (error) {
      console.error('❌ Error exporting to Excel:', error)
      throw error
    }
  }

  /**
   * Get report title by type
   */
  getReportTitle(reportType) {
    const titles = {
      'sales-summary': 'Sales Summary',
      'payments': 'Payments Report',
      'product-summary': 'Product Summary',
      'stock-alerts': 'Stock Alerts',
      'raw-material-summary': 'Raw Material Summary',
      'product-margin': 'Product Margin',
      'suppliers': 'Suppliers Report',
      'customers': 'Customers Report',
      'employees': 'Employees Report',
      'salary-reports': 'Salary Reports',
      'product-sales': 'Product Sales',
      'stock-expiry': 'Stock Expiry',
      'customer-returns': 'Customer Returns',
      'supplier-returns': 'Supplier Returns',
      'profits-loss': 'Profits/Loss Statement'
    }
    return titles[reportType] || reportType
  }

  /**
   * Get column configuration for each report type
   */
  getReportColumns(reportType) {
    const columnMapping = {
      'sales-summary': [
        { key: 'date', label: 'Date' },
        { key: 'orderNo', label: 'Order No' },
        { key: 'customer', label: 'Customer' },
        { key: 'type', label: 'Type' },
        { key: 'totalBill', label: 'Total Bill' },
        { key: 'paymentMethod', label: 'Payment Method' }
      ],
      'payments': [
        { key: 'date', label: 'Date' },
        { key: 'orderNo', label: 'Order No' },
        { key: 'customer', label: 'Customer' },
        { key: 'totalBill', label: 'Amount' },
        { key: 'paymentType', label: 'Payment Type' },
        { key: 'paymentStatus', label: 'Status' }
      ],
      'product-summary': [
        { key: 'code', label: 'Code' },
        { key: 'productName', label: 'Product' },
        { key: 'category', label: 'Category' },
        { key: 'currentStock', label: 'Stock' },
        { key: 'purchasePrice', label: 'Purchase Price' },
        { key: 'salesPrice', label: 'Sales Price' }
      ],
      'stock-alerts': [
        { key: 'itemName', label: 'Product' },
        { key: 'currentStock', label: 'Current Stock' },
        { key: 'threshold', label: 'Min Stock' },
        { key: 'status', label: 'Status' },
        { key: 'branchName', label: 'Branch' }
      ],
      'customers': [
        { key: 'customerName', label: 'Customer' },
        { key: 'mobileNumber', label: 'Phone' },
        { key: 'loyaltyPoints', label: 'Loyalty Points' },
        { key: 'totalOrders', label: 'Orders' },
        { key: 'totalPurchase', label: 'Total Spent' }
      ],
      'employees': [
        { key: 'employeeName', label: 'Employee' },
        { key: 'role', label: 'Role' },
        { key: 'branch', label: 'Branch' },
        { key: 'status', label: 'Status' }
      ],
      'suppliers': [
        { key: 'name', label: 'Supplier Name' },
        { key: 'contactPerson', label: 'Contact Person' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'email', label: 'Email' },
        { key: 'totalPurchases', label: 'Total Purchases' },
        { key: 'isActive', label: 'Status' }
      ],
      'raw-material-summary': [
        { key: 'code', label: 'Code' },
        { key: 'name', label: 'Name' },
        { key: 'unit', label: 'Unit' },
        { key: 'currentStock', label: 'Stock' },
        { key: 'branchName', label: 'Branch' }
      ],
      'rawMaterialSummary': [
        { key: 'code', label: 'Code' },
        { key: 'name', label: 'Name' },
        { key: 'unit', label: 'Unit' },
        { key: 'currentStock', label: 'Stock' },  
        { key: 'branchName', label: 'Branch' }
      ],
      'product-margin': [
        { key: 'productName', label: 'Product' },
        { key: 'category', label: 'Category' },
        { key: 'costPrice', label: 'Cost Price' },
        { key: 'sellingPrice', label: 'Selling Price' },
        { key: 'margin', label: 'Margin' },
        { key: 'marginPercentage', label: 'Margin %' }
      ],
      'salary-reports': [
        { key: 'employeeName', label: 'Employee' },
        { key: 'designation', label: 'Designation' },
        { key: 'basicSalary', label: 'Basic Salary' },
        { key: 'allowances', label: 'Allowances' },
        { key: 'deductions', label: 'Deductions' },
        { key: 'netSalary', label: 'Net Salary' }
      ],
      'product-sales': [
        { key: 'productName', label: 'Product' },
        { key: 'category', label: 'Category' },
        { key: 'quantitySold', label: 'Quantity Sold' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'date', label: 'Date' }
      ],
      'stock-expiry': [
        { key: 'itemName', label: 'Item' },
        { key: 'batch', label: 'Batch' },
        { key: 'expiryDate', label: 'Expiry Date' },
        { key: 'currentStock', label: 'Stock' },
        { key: 'status', label: 'Status' }
      ],
      'customer-returns': [
        { key: 'date', label: 'Date' },
        { key: 'customerName', label: 'Customer' },
        { key: 'productName', label: 'Product' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'amount', label: 'Amount' },
        { key: 'reason', label: 'Reason' }
      ],
      'supplier-returns': [
        { key: 'date', label: 'Date' },
        { key: 'supplierName', label: 'Supplier' },
        { key: 'productName', label: 'Product' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'amount', label: 'Amount' },
        { key: 'reason', label: 'Reason' }
      ],
      'profits-loss': [
        { key: 'date', label: 'Date' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'costs', label: 'Costs' },
        { key: 'profit', label: 'Profit' },
        { key: 'profitMargin', label: 'Profit Margin %' }
      ]
    }
    
    return columnMapping[reportType] || []
  }

  /**
   * Create manual table when autoTable is not available
   */
  createManualTable(doc, headers, tableData, startY) {
    try {
      console.log('📊 Creating manual table with headers:', headers)
      console.log('📊 Headers count:', headers.length)
      console.log('📊 Table data rows:', tableData.length)
      
      if (!headers || headers.length === 0) {
        console.error('❌ No headers provided to createManualTable')
        return
      }
      
      const pageWidth = doc.internal.pageSize.getWidth()
      const tableWidth = pageWidth - 80 // 40px margins on each side
      const colWidth = tableWidth / headers.length
      let yPos = startY
      
      console.log('📊 Table dimensions:', { pageWidth, tableWidth, colWidth, startY: yPos })
      
      // Draw headers
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setFillColor(249, 115, 22) // Orange background
      doc.setTextColor(255, 255, 255) // White text
      
      // Draw each header cell individually
      headers.forEach((header, colIndex) => {
        const x = 40 + (colIndex * colWidth)
        
        // Draw individual cell background and border
        doc.rect(x, yPos, colWidth, 20, 'F') // Fill cell
        doc.rect(x, yPos, colWidth, 20, 'S') // Stroke cell border
        
        // Center text in cell
        const headerText = String(header || 'Column')
        const textWidth = doc.getTextWidth(headerText)
        const textX = x + (colWidth - textWidth) / 2
        doc.text(headerText, textX, yPos + 13)
        
        console.log(`📋 Header ${colIndex}: "${headerText}" at x=${textX}, cellX=${x}, width=${colWidth}`)
      })
      
      yPos += 20
      
      // Draw data rows
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0, 0, 0) // Black text
      
      console.log('📊 Drawing', tableData.length, 'data rows')
      
      tableData.forEach((row, rowIndex) => {
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage()
          yPos = 40
          
          // Redraw headers on new page
          doc.setFont('helvetica', 'bold')
          doc.setFillColor(249, 115, 22)
          doc.setTextColor(255, 255, 255)
          
          headers.forEach((header, colIndex) => {
            const x = 40 + (colIndex * colWidth)
            
            // Draw individual cell background and border
            doc.rect(x, yPos, colWidth, 20, 'F') // Fill cell
            doc.rect(x, yPos, colWidth, 20, 'S') // Stroke cell border
            
            const headerText = String(header || 'Column')
            const textWidth = doc.getTextWidth(headerText)
            const textX = x + (colWidth - textWidth) / 2
            doc.text(headerText, textX, yPos + 13)
          })
          
          yPos += 20
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(0, 0, 0)
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 1) {
          doc.setFillColor(249, 249, 249) // Light gray
          doc.rect(40, yPos, tableWidth, 15, 'F')
        }
        
        // Draw cell data with individual cell borders
        row.forEach((cellData, colIndex) => {
          const x = 40 + (colIndex * colWidth)
          
          // Draw individual cell border
          doc.rect(x, yPos, colWidth, 15, 'S')
          
          // Truncate text if too long
          let displayText = String(cellData || '')
          const maxWidth = colWidth - 6 // 3px padding on each side
          
          while (doc.getTextWidth(displayText) > maxWidth && displayText.length > 0) {
            displayText = displayText.substring(0, displayText.length - 1)
          }
          
          doc.text(displayText, x + 3, yPos + 10)
        })
        
        yPos += 15
      })
      
      console.log('✅ Manual table created successfully')
      
    } catch (error) {
      console.error('❌ Error creating manual table:', error)
    }
  }

  /**
   * Debug function to test PDF export
   */
  debugPDFExport() {
    console.log('🧪 Debug PDF Export Test')
    
    // Test data
    const testData = {
      'payments': [
        { date: '2025-09-30', orderNo: 'ORD001', customer: 'Test Customer', totalBill: 100, paymentType: 'Cash', paymentStatus: 'Paid' }
      ]
    }
    
    const testReports = ['payments']
    const testFilters = {}
    
    console.log('🧪 Test data:', testData)
    console.log('🧪 Test reports:', testReports)
    
    // Test column mapping
    const columns = this.getReportColumns('payments')
    console.log('🧪 Columns for payments:', columns)
    
    return this.exportToPDF(testData, testReports, testFilters)
  }
}

// Make debug function available globally
window.debugPDFExport = () => {
  const service = new ReportsService()
  return service.debugPDFExport()
}

export default new ReportsService()